/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Layers, 
  Users, 
  MapPin, 
  Package, 
  Video, 
  Calendar, 
  FileText, 
  LogOut, 
  UserCheck, 
  Truck, 
  Sparkles, 
  RefreshCw,
  Clock,
  LayoutDashboard,
  ShieldAlert,
  Sliders,
  CheckCircle2,
  Lock,
  Mail,
  User,
  ExternalLink,
  Shield,
  Folder,
  FolderOpen,
  FolderLock,
  ChevronDown,
  ChevronRight,
  Plus,
  Compass,
  FileCheck,
  History,
  AlertOctagon,
  Terminal,
  Activity,
  Award,
  CalendarDays,
  LockKeyhole,
  CheckCircle,
  HelpCircle,
  Wrench,
  Archive,
  GraduationCap,
  Hammer,
  Bell,
  Workflow,
  Search,
  BookOpen,
  Home,
  Wifi,
  WifiOff,
  Download,
  Menu,
  X,
  MessageSquare,
  MessageCircle,
  Camera,
  Save,
  ArrowLeft,
  QrCode,
  Printer,
  BarChart3
} from 'lucide-react';

// Data service and Types
import { dataService } from './lib/dataService.ts';
import { Client, CostCenter, Destination, GoodsItem, Folio, PreInvoice, CalendarEvent, MeetMeeting, UserProfile, isClientRole, isClientUser, isChoferUser, isChoferRole, isInternalRole, isAdminRole, isClientOperatorUser, shouldHidePricesForUser, PendingAction, ReworkIncident } from './types.ts';

// Modular Visual Components
import DashboardStats from './components/DashboardStats.tsx';
import FolioManager from './components/FolioManager.tsx';
import ClientConfig from './components/ClientConfig.tsx';
import GoodsCatalog from './components/GoodsCatalog.tsx';
import MeetScheduler from './components/MeetScheduler.tsx';
import LogisticsCalendar from './components/LogisticsCalendar.tsx';
import LogisticsMap from './components/LogisticsMap.tsx';
import BillingManager from './components/BillingManager.tsx';
import AdminPortal from './components/AdminPortal.tsx';
import WelcomePage from './components/WelcomePage.tsx';
import KpisManager from './components/KpisManager.tsx';

// Submenu Components
import PoHistoryPortal from './components/PoHistoryPortal.tsx';
import AlertsAndNotifications from './components/AlertsAndNotifications.tsx';
import PublicTrackingPortal from './components/PublicTrackingPortal.tsx';
import TraceabilityStationManager from './components/TraceabilityStationManager.tsx';
import ClientChatModule from './components/ClientChatModule.tsx';
import ReworkAndReturnsManager from './components/ReworkAndReturnsManager.tsx';
import SecurityInternalManager from './components/SecurityInternalManager.tsx';
import { ProjectsManager } from './components/ProjectsManager.tsx';
import { DC3NotificationConfigModal } from './components/DC3NotificationConfigModal.tsx';
import { DC3NotificationSettings } from './types.ts';
import { 
  loadAllDC3RecordsFromStorage, 
  evaluateDC3Records, 
  dispatchDC3NotificationAlerts, 
  getDefaultDC3NotificationSettings 
} from './utils/dc3NotificationHelper.ts';
import { 
  loadAllCertificatesFromStorage, 
  evaluateCertificatesAlerts, 
  CertificatesAlertEvaluation 
} from './utils/certificateAlertHelper.ts';
import { CertificatesExpirationBanner } from './components/CertificatesExpirationBanner.tsx';
import { NotificationService } from './lib/notificationService.ts';
import { fcmService } from './lib/fcmService.ts';

type TabType = 'welcome' | 'kpis' | 'dashboard' | 'folios' | 'clients' | 'catalog' | 'meetings' | 'calendar' | 'billing' | 'admin_portal' | 'client_submenu' | 'internal_submenu';

const APP_LOGO_URL = 'https://www.grupoisn.com.mx/wp-content/uploads/2025/12/cropped-WhatsApp-Image-2025-12-19-at-12.31.53.jpeg';

function FuturisticHeaderClock() {
  const [time, setTime] = useState('');
  useEffect(() => {
    const update = () => {
      const now = new Date();
      setTime(now.toLocaleTimeString('es-MX', { hour12: false }) + ' CST');
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, []);
  return <span className="font-mono text-xs tracking-widest text-cyan-400 font-bold glow-cyan">{time}</span>;
}

export default function App() {
  // Global App States loaded dynamically from Firestore & localStorage
  const [clients, setClients] = useState<Client[]>([]);
  const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
  const [destinations, setDestinations] = useState<Destination[]>([]);
  const [goods, setGoods] = useState<GoodsItem[]>([]);
  const [folios, setFolios] = useState<Folio[]>([]);
  const [preInvoices, setPreInvoices] = useState<PreInvoice[]>([]);
  const [meetings, setMeetings] = useState<MeetMeeting[]>([]);
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);

  // Refs and first-load flag to compare real-time changes
  const [isFirstLoadFinished, setIsFirstLoadFinished] = useState(false);
  const isFirstLoadFinishedRef = useRef(false);
  const clientsRef = useRef<Client[]>([]);
  const costCentersRef = useRef<CostCenter[]>([]);
  const destinationsRef = useRef<Destination[]>([]);
  const foliosRef = useRef<Folio[]>([]);
  const preInvoicesRef = useRef<PreInvoice[]>([]);
  const meetingsRef = useRef<MeetMeeting[]>([]);
  const calendarEventsRef = useRef<CalendarEvent[]>([]);
  const incidentsRef = useRef<ReworkIncident[]>([]);

  // Update isFirstLoadFinishedRef when state changes
  useEffect(() => {
    isFirstLoadFinishedRef.current = isFirstLoadFinished;
  }, [isFirstLoadFinished]);

  // PWA, Offline and Notification States
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isAppInstalled, setIsAppInstalled] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState<NotificationPermission>(
    'Notification' in window ? Notification.permission : 'denied'
  );
  const [fcmAlertBanner, setFcmAlertBanner] = useState<{ title: string; body: string; folioId?: string } | null>(null);
  
  // Pending Actions Queue state for offline changes syncing
  const [pendingActions, setPendingActions] = useState<PendingAction[]>([]);

  // Subscribe to Pending Actions Queue & 5-Second Automatic Sync Loop
  useEffect(() => {
    const unsub = dataService.subscribeToPendingActions((queue) => {
      setPendingActions(queue);
    });

    // Automatic 5-second sync loop
    const autoSyncInterval = setInterval(() => {
      if (navigator.onLine) {
        dataService.syncPendingActions();
      }
    }, 5000);

    return () => {
      unsub();
      clearInterval(autoSyncInterval);
    };
  }, []);

  // Synchronous Monitor of Network, PWA Install and Native notifications events
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      triggerNativeNotification('Sistema Conectado', 'Se ha restablecido la conexión. Sincronizando acciones pendientes...');
      dataService.syncPendingActions();
    };
    const handleOffline = () => {
      setIsOnline(false);
      triggerNativeNotification('Modo Fuera de Línea Activado', 'Operando en modo local seguro 24/7. Tu información se encuentra completamente protegida.');
      const localFolios = localStorage.getItem('snfsic_folios') || localStorage.getItem('trazalog_folios');
      if (localFolios) {
        try {
          setFolios(JSON.parse(localFolios));
        } catch (e) {
          console.warn("Failed to load local folios on offline transition:", e);
        }
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    const handleAppInstalled = () => {
      setIsAppInstalled(true);
      setDeferredPrompt(null);
      triggerNativeNotification('¡Instalación Exitosa!', 'SNF - SIC ahora está disponible en tu pantalla de inicio como aplicación nativa.');
    };
    window.addEventListener('appinstalled', handleAppInstalled);

    // FCM foreground critical push listener
    const handleFcmAlert = (e: any) => {
      const detail = e.detail;
      if (detail) {
        setFcmAlertBanner({
          title: detail.title || '🚨 Alerta Crítica FCM',
          body: detail.body || 'Se ha recibido una notificación push en tiempo real.',
          folioId: detail.payload?.data?.folioId || detail.payload?.folioId
        });
      }
    };
    window.addEventListener('fcm_critical_alert', handleFcmAlert);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      window.removeEventListener('fcm_critical_alert', handleFcmAlert);
    };
  }, []);

  const triggerNativeNotification = (title: string, body: string) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      try {
        navigator.serviceWorker.ready.then((registration) => {
          registration.showNotification(title, {
            body,
            icon: 'https://www.grupoisn.com.mx/wp-content/uploads/2025/12/cropped-WhatsApp-Image-2025-12-19-at-12.31.53.jpeg',
            badge: 'https://www.grupoisn.com.mx/wp-content/uploads/2025/12/cropped-WhatsApp-Image-2025-12-19-at-12.31.53.jpeg',
            vibrate: [100, 50, 100],
          } as any);
        }).catch(() => {
          new Notification(title, { body });
        });
      } catch (e) {
        new Notification(title, { body });
      }
    }
  };

  const requestNotificationPermission = async () => {
    if (!(window as any).Notification) {
      await (window as any).showAppAlert('Este navegador no soporta notificaciones nativas.', 'Notificaciones No Disponibles');
      return;
    }
    const permission = await Notification.requestPermission();
    setNotificationPermission(permission);
    if (permission === 'granted') {
      const tok = await fcmService.requestFCMToken('Personal Operativo', 'OPERADOR');
      if (tok) {
        triggerNativeNotification('¡Alertas Push FCM Activadas!', 'Dispositivo registrado exitosamente en Firebase Cloud Messaging (FCM).');
      } else {
        triggerNativeNotification('¡Alertas Nativas Activadas!', 'Recibirás avisos instantáneos sobre cambios de estado en folios y prefacturas.');
      }
    }
  };

  const handleInstallApp = async () => {
    if (!deferredPrompt) {
      await (window as any).showAppAlert('La aplicación ya está instalada o tu navegador no es compatible con la instalación directa de PWAs.', 'Instalador de PWA');
      return;
    }
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    console.log(`User response to install prompt: ${outcome}`);
    setDeferredPrompt(null);
  };
  
  // Real FX Sourced dynamically on load
  const [exchangeRate, setExchangeRate] = useState(19.45);
  const [lastUpdatedRate, setLastUpdatedRate] = useState(new Date().toISOString());

  // Navigation and Auth states
  const [activeTab, setActiveTab] = useState<TabType>('welcome');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSyncQueueOpen, setIsSyncQueueOpen] = useState(false);
  const [logisticsSubtab, setLogisticsSubtab] = useState<'calendar' | 'map'>('calendar');
  const [selectedCostCenterFilter, setSelectedCostCenterFilter] = useState<string | null>(null);
  
  // Custom states for the nested folders structure
  const [selectedCostCenter, setSelectedCostCenter] = useState<CostCenter | null>(null);
  const [isCostCenterDropdownOpen, setIsCostCenterDropdownOpen] = useState(false);
  const [expandedClientSidebarId, setExpandedClientSidebarId] = useState<string | null>(null);
  const [expandedInternalCcId, setExpandedInternalCcId] = useState<string | null>(null);
  const [activeInternalCostCenterId, setActiveInternalCostCenterId] = useState<string | null>(null);
  const [collapsedClients, setCollapsedClients] = useState<Record<string, boolean>>({});
  const [activeClientSubmenu, setActiveClientSubmenu] = useState<string>('dashboard');
  const [activeInternalSubmenu, setActiveInternalSubmenu] = useState<string | null>(null);
  const [activeProjectsSubtab, setActiveProjectsSubtab] = useState<'list' | 'workplan'>('list');
  const [isProjectsSubmenuOpen, setIsProjectsSubmenuOpen] = useState(false);
  const [isGlobalFolderOpen, setIsGlobalFolderOpen] = useState(true);
  const [isClientesFolderOpen, setIsClientesFolderOpen] = useState(true);
  const [isInternoFolderOpen, setIsInternoFolderOpen] = useState(false);

  // Auto-close mobile menu on view change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [activeTab, activeClientSubmenu, activeInternalSubmenu]);
  
  // Referenced item inside alerts/notifications
  const [referencedItemId, setReferencedItemId] = useState<string | null>(null);

  // Global App Custom Modals for alert and confirm
  const [modalAlert, setModalAlert] = useState<{
    show: boolean;
    title: string;
    message: string;
    resolve?: () => void;
  }>({ show: false, title: '', message: '' });

  const [modalConfirm, setModalConfirm] = useState<{
    show: boolean;
    title: string;
    message: string;
    resolve?: (val: boolean) => void;
  }>({ show: false, title: '', message: '' });

  useEffect(() => {
    window.showAppAlert = (message: string, title?: string) => {
      return new Promise<void>((resolve) => {
        setModalAlert({
          show: true,
          title: title || 'Notificación del Sistema',
          message,
          resolve: () => {
            setModalAlert(prev => ({ ...prev, show: false }));
            resolve();
          }
        });
      });
    };

    window.showAppConfirm = (message: string, title?: string) => {
      return new Promise<boolean>((resolve) => {
        setModalConfirm({
          show: true,
          title: title || 'Confirmar Acción',
          message,
          resolve: (val: boolean) => {
            setModalConfirm(prev => ({ ...prev, show: false }));
            resolve(val);
          }
        });
      });
    };
  }, []);

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState<UserProfile | null>(null);
  const [trackedFolioId, setTrackedFolioId] = useState<string | null>(null);
  const [publicTerminalParams, setPublicTerminalParams] = useState<{
    terminalFase?: number;
    folioId?: string;
    clientId?: string;
  } | null>(null);

  useEffect(() => {
    // URL Tracking Params
    const params = new URLSearchParams(window.location.search);
    const trackId = params.get('track') || params.get('tracking') || params.get('folio') || params.get('id');
    const terminal = params.get('terminal') || params.get('fase') || params.get('station') || params.get('qr') || params.get('stage');
    const client = params.get('client');

    let faseNum: number | undefined = undefined;
    if (terminal) {
      const match = terminal.match(/\d+/);
      if (match) {
        const num = parseInt(match[0], 10);
        if (!isNaN(num) && num >= 0 && num <= 6) {
          faseNum = num;
        }
      }
    }

    if (trackId || faseNum !== undefined || params.get('public') === 'true') {
      setPublicTerminalParams({
        terminalFase: faseNum,
        folioId: trackId ? trackId.trim().toUpperCase() : undefined,
        clientId: client || undefined,
      });
      if (trackId) {
        setTrackedFolioId(trackId.trim().toUpperCase());
      }
    }

    // Session Restore
    try {
      const savedUserJson = localStorage.getItem('snfsic_logged_user');
      if (savedUserJson) {
        const savedUser = JSON.parse(savedUserJson);
        if (savedUser && savedUser.email) {
          setCurrentUser(savedUser);
          setIsLoggedIn(true);
        }
      }
    } catch (e) {
      console.warn("Could not restore logged user session", e);
    }
  }, []);

  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [isDC3ConfigModalOpen, setIsDC3ConfigModalOpen] = useState(false);
  const profileDropdownRef = useRef<HTMLDivElement>(null);
  const [tempName, setTempName] = useState('');
  const [tempEmail, setTempEmail] = useState('');
  const [tempPhone, setTempPhone] = useState('');
  const [tempAvatarUrl, setTempAvatarUrl] = useState('');

  // Aggregated DC-3 records across cost centers for notification engine
  const allDC3Records = useMemo(() => {
    return loadAllDC3RecordsFromStorage(costCenters);
  }, [costCenters, isDC3ConfigModalOpen]);

  // Real-time Certificates Expiration Alert Evaluation (<30 days verification)
  const [certAlertEvaluation, setCertAlertEvaluation] = useState<CertificatesAlertEvaluation>(() => {
    const certs = loadAllCertificatesFromStorage(costCenters);
    return evaluateCertificatesAlerts(certs, 30, costCenters);
  });

  // STARTUP VERIFICATION EFFECT: Compare current date with certificate expiration dates on app initialization
  useEffect(() => {
    const performStartupCertificateCheck = () => {
      const allCerts = loadAllCertificatesFromStorage(costCenters);
      const evalResult = evaluateCertificatesAlerts(allCerts, 30, costCenters);
      setCertAlertEvaluation(evalResult);

      // Trigger visual alert & notification if urgent certificates are detected (<30 days or expired)
      if (evalResult.hasUrgentAlerts && currentUser) {
        console.log(`[HSEQ Startup Check] ${evalResult.urgentTotalCount} certificate(s) expiring within 30 days or expired.`);
        
        // Native / push notification dispatch if supported and permitted
        try {
          if ('Notification' in window && Notification.permission === 'granted') {
            const isSevere = evalResult.expiredCount > 0;
            const title = isSevere 
              ? `🚨 SNF HSEQ: ${evalResult.expiredCount} Certificación(es) Vencida(s)`
              : `⚠️ SNF HSEQ: ${evalResult.expiringCount} Certificación(es) por Vencer (<30 días)`;
            const body = isSevere
              ? `Se detectaron ${evalResult.urgentTotalCount} documentos que requieren renovación inmediata o auditoría urgente.`
              : `Hay ${evalResult.expiringCount} certificaciones o auditorías que vencen en menos de 30 días.`;
            
            new Notification(title, {
              body,
              icon: APP_LOGO_URL,
              tag: 'hseq_cert_startup_alert'
            });
          }
        } catch (e) {
          console.warn('[HSEQ Notification Native Error]', e);
        }
      }
    };

    // Run startup check after short hydration delay
    const initTimer = setTimeout(performStartupCertificateCheck, 1500);

    // Subscribe to runtime updates when certificates are added, modified or deleted
    const handleCertsUpdate = () => {
      const updatedCerts = loadAllCertificatesFromStorage(costCenters);
      setCertAlertEvaluation(evaluateCertificatesAlerts(updatedCerts, 30, costCenters));
    };

    window.addEventListener('snf_certificates_updated', handleCertsUpdate);
    window.addEventListener('storage', handleCertsUpdate);

    return () => {
      clearTimeout(initTimer);
      window.removeEventListener('snf_certificates_updated', handleCertsUpdate);
      window.removeEventListener('storage', handleCertsUpdate);
    };
  }, [costCenters, currentUser?.uid]);

  // Periodic and on-mount evaluation for DC-3 notification alerts
  useEffect(() => {
    if (!currentUser) return;
    const settings = currentUser.dc3NotificationSettings || getDefaultDC3NotificationSettings();
    if (!settings.enabled) return;

    // Run initial evaluation check with a slight delay to allow complete data hydration
    const timer = setTimeout(() => {
      const records = loadAllDC3RecordsFromStorage(costCenters);
      const evalResult = evaluateDC3Records(records, settings, costCenters);
      if (evalResult.alertItems.length > 0) {
        dispatchDC3NotificationAlerts(evalResult, settings, currentUser).catch(err => {
          console.warn('[DC3 Notification Auto-Dispatch Error]:', err);
        });
      }
    }, 4000);

    return () => clearTimeout(timer);
  }, [currentUser?.uid, costCenters]);

  const handleSaveDC3NotificationSettings = async (newSettings: DC3NotificationSettings) => {
    if (currentUser) {
      const updatedUser: UserProfile = {
        ...currentUser,
        dc3NotificationSettings: newSettings
      };
      setCurrentUser(updatedUser);
      try {
        localStorage.setItem('snfsic_logged_user', JSON.stringify(updatedUser));
      } catch (e) {
        console.warn('Failed caching user profile', e);
      }
      await dataService.saveUser(updatedUser);
    }
    try {
      localStorage.setItem('snfsic_dc3_notif_config', JSON.stringify(newSettings));
    } catch (e) {
      console.warn('Failed caching dc3 notification config', e);
    }
  };

  // Always ensure the profile bubble is collapsed whenever login status or user session changes
  useEffect(() => {
    setIsProfileDropdownOpen(false);
  }, [isLoggedIn, currentUser?.uid, currentUser?.email]);

  // Click outside and Escape key handler to collapse profile bubble popover automatically
  useEffect(() => {
    if (!isProfileDropdownOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target as Node)) {
        setIsProfileDropdownOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsProfileDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isProfileDropdownOpen]);

  // Sync temp state with currentUser whenever profile panel is toggled open or currentUser updates
  useEffect(() => {
    if (currentUser) {
      setTempName(currentUser.name || '');
      setTempEmail(currentUser.email || '');
      setTempPhone(currentUser.phone || '');
      setTempAvatarUrl(currentUser.avatarUrl || '');
    }
  }, [currentUser, isProfileDropdownOpen]);

  // Derived list of cost centers accessible to the current user
  const allowedCostCenters = useMemo(() => {
    if (!currentUser) return costCenters;
    
    // Only Administrators have unrestricted access to all cost centers
    if (isAdminRole(currentUser.role) || currentUser.role?.toLowerCase() === 'admin' || currentUser.role?.toLowerCase() === 'administrador') {
      return costCenters;
    }

    // All other internal roles (GERENTE, COORDINADOR, SUPERVISOR, OPERADOR, CHOFER) and client roles
    // MUST ONLY see their explicitly assigned cost centers
    if (currentUser.assignedCostCenterIds && currentUser.assignedCostCenterIds.length > 0) {
      const assignedIds = new Set(currentUser.assignedCostCenterIds.map(id => id.trim().toLowerCase()));
      const filtered = costCenters.filter(cc => 
        (cc.id && assignedIds.has(cc.id.toLowerCase())) || 
        (cc.code && assignedIds.has(cc.code.toLowerCase())) || 
        (cc.name && assignedIds.has(cc.name.toLowerCase()))
      );
      return filtered;
    }

    // If client user without explicit assignedCostCenterIds, derive from client configuration
    if (isClientUser(currentUser)) {
      const userClient = clients.find(c => 
        (currentUser?.clientCode && (c.code === currentUser.clientCode || c.id === currentUser.clientCode || c.name === currentUser.clientCode)) ||
        (currentUser?.assignedClientIds && (currentUser.assignedClientIds.includes(c.id) || currentUser.assignedClientIds.includes(c.code))) ||
        (currentUser?.name && c.name.toLowerCase().includes(currentUser.name.toLowerCase()))
      );
      if (userClient) {
        return costCenters.filter(cc => 
          (userClient.costCenterIds && userClient.costCenterIds.includes(cc.id)) ||
          cc.clientId === userClient.id || cc.clientId === userClient.code ||
          (cc.clientIds && (cc.clientIds.includes(userClient.id) || cc.clientIds.includes(userClient.code)))
        );
      }
    }

    // If a non-admin user has NO cost centers assigned, they cannot see any cost center
    return [];
  }, [costCenters, currentUser, clients]);

  // Automatically lock / scope cost center for non-admin users so they only access their assigned cost center(s)
  useEffect(() => {
    if (!currentUser) return;
    
    if (!isAdminRole(currentUser.role) && currentUser.role?.toLowerCase() !== 'admin' && currentUser.role?.toLowerCase() !== 'administrador') {
      if (allowedCostCenters.length > 0) {
        if (!selectedCostCenter || !allowedCostCenters.some(cc => cc.id === selectedCostCenter.id)) {
          setSelectedCostCenter(allowedCostCenters[0]);
          setSelectedCostCenterFilter(allowedCostCenters[0].id);
          setActiveInternalCostCenterId(allowedCostCenters[0].id);
        }
      } else {
        setSelectedCostCenter(null);
        setSelectedCostCenterFilter(null);
      }
    }
  }, [currentUser, allowedCostCenters, selectedCostCenter]);

  // Active Cost Center specifically for the INTERNO folder and submodules (strictly tied to the top selected Cost Center)
  const activeInternalCostCenter = useMemo(() => {
    if (selectedCostCenter && (!currentUser || isAdminRole(currentUser.role) || allowedCostCenters.some(cc => cc.id === selectedCostCenter.id))) {
      return selectedCostCenter;
    }
    if (allowedCostCenters.length > 0) return allowedCostCenters[0];
    return null;
  }, [selectedCostCenter, allowedCostCenters, currentUser]);

  // Derived list of clients associated with the currently scoped view / selectedCostCenter
  const availableClientsForSidebar = useMemo(() => {
    return clients.filter((client) => {
      // Strict isolation: client & chofer users can ONLY see their assigned client
      if (isClientUser(currentUser) || isChoferUser(currentUser)) {
        const clientMatches = (
          (currentUser?.clientCode && (client.code === currentUser.clientCode || client.id === currentUser.clientCode || client.name === currentUser.clientCode)) ||
          (currentUser?.assignedClientIds && (currentUser.assignedClientIds.includes(client.id) || currentUser.assignedClientIds.includes(client.code))) ||
          (currentUser?.assignedCostCenterIds && client.costCenterIds && client.costCenterIds.some(ccid => currentUser.assignedCostCenterIds?.includes(ccid))) ||
          (currentUser?.name && client.name.toLowerCase().includes(currentUser.name.toLowerCase()))
        );
        return clientMatches;
      }

      // Non-admin internal users: restrict strictly to clients belonging to their allowedCostCenters
      if (!isAdminRole(currentUser?.role) && currentUser?.role !== 'admin' && currentUser?.role !== 'administrador') {
        if (currentUser?.assignedClientIds && currentUser.assignedClientIds.length > 0) {
          if (!currentUser.assignedClientIds.includes(client.id) && !currentUser.assignedClientIds.includes(client.code)) {
            return false;
          }
        }
        if (allowedCostCenters.length > 0) {
          const allowedCcIds = new Set(allowedCostCenters.map(c => c.id));
          const allowedCcCodes = new Set(allowedCostCenters.map(c => c.code));
          const isLinkedToAllowedCc = 
            (client.costCenterIds && client.costCenterIds.some(ccid => allowedCcIds.has(ccid) || allowedCcCodes.has(ccid))) ||
            allowedCostCenters.some(cc => 
              cc.clientId === client.id || 
              cc.clientId === client.code ||
              (cc.clientIds && (cc.clientIds.includes(client.id) || cc.clientIds.includes(client.code)))
            );
          if (!isLinkedToAllowedCc) {
            return false;
          }
        } else {
          return false;
        }
      }

      // Filter if a cost center / sede is selected (e.g. Bajío, San Luis Potosí, etc.)
      if (selectedCostCenter) {
        const cc = selectedCostCenter;
        const isPrimaryMatch = cc.clientId && (
          client.id === cc.clientId || 
          client.code === cc.clientId ||
          client.id.toLowerCase() === cc.clientId.toLowerCase() ||
          (client.code && cc.clientId.toLowerCase().includes(client.code.toLowerCase()))
        );
        const isMultipleMatch = cc.clientIds && (
          cc.clientIds.includes(client.id) || 
          cc.clientIds.includes(client.code)
        );
        const isClientCcMatch = client.costCenterIds && (
          client.costCenterIds.includes(cc.id)
        );
        if (!isPrimaryMatch && !isMultipleMatch && !isClientCcMatch) {
          return false;
        }
      }
      return true;
    });
  }, [clients, currentUser, allowedCostCenters, selectedCostCenter]);

  // When a Cost Center without client mandante is active, automatically show and expand INTERNO folder
  useEffect(() => {
    if (selectedCostCenter) {
      const cc = selectedCostCenter;
      const hasLinkedClients = clients.some(client => {
        const isPrimaryMatch = cc.clientId && (
          client.id === cc.clientId || 
          client.code === cc.clientId ||
          client.id.toLowerCase() === cc.clientId.toLowerCase() ||
          (client.code && cc.clientId.toLowerCase().includes(client.code.toLowerCase()))
        );
        const isMultipleMatch = cc.clientIds && (
          cc.clientIds.includes(client.id) || 
          cc.clientIds.includes(client.code)
        );
        const isClientCcMatch = client.costCenterIds && (
          client.costCenterIds.includes(cc.id)
        );
        return Boolean(isPrimaryMatch || isMultipleMatch || isClientCcMatch);
      });

      if (!hasLinkedClients) {
        setIsInternoFolderOpen(true);
        setIsClientesFolderOpen(false);
        setExpandedClientSidebarId(null);
        if (activeTab === 'client_submenu') {
          setActiveTab('internal_submenu');
          if (!activeInternalSubmenu) {
            setActiveInternalSubmenu('proyectos');
          }
        }
      }
    }
  }, [selectedCostCenter, clients, activeTab, activeInternalSubmenu]);

  const handleAvatarFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        window.showAppAlert('La imagen es demasiado grande. El límite de tamaño es de 2MB.', 'Imagen Excesiva');
        return;
      }
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target?.result && typeof event.target.result === 'string') {
          setTempAvatarUrl(event.target.result);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCancelProfileEdit = () => {
    if (currentUser) {
      setTempName(currentUser.name || '');
      setTempEmail(currentUser.email || '');
      setTempPhone(currentUser.phone || '');
      setTempAvatarUrl(currentUser.avatarUrl || '');
    }
    setIsProfileDropdownOpen(false);
  };

  const handleSaveProfileEdit = async () => {
    if (!currentUser) return;
    if (!tempName.trim()) {
      await window.showAppAlert('El nombre completo no puede estar vacío.', 'Falta Información');
      return;
    }
    if (!tempEmail.trim()) {
      await window.showAppAlert('El correo electrónico no puede estar vacío.', 'Falta Información');
      return;
    }

    try {
      const updatedUser: UserProfile = {
        ...currentUser,
        name: tempName.trim(),
        email: tempEmail.trim(),
        phone: tempPhone.trim(),
        avatarUrl: tempAvatarUrl.trim()
      };

      setCurrentUser(updatedUser);
      await dataService.saveUser(updatedUser);
      
      setIsProfileDropdownOpen(false);
      await window.showAppAlert('Tu perfil personal ha sido actualizado correctamente.', 'Perfil Actualizado');
      await handleLogActivity('PERFIL_ACTUALIZADO', `El usuario actualizó su información de perfil (${updatedUser.name}).`);
    } catch (err) {
      console.error('Error saving user profile:', err);
      await window.showAppAlert('Ocurrió un error al guardar los cambios: ' + err, 'Error de Operación');
    }
  };

  // Loading indicator state
  const [isLoading, setIsLoading] = useState(true);

  // Dynamic Login form states (strictly blank by default, never store/retain passwords)
  const [loginEmail, setLoginEmail] = useState('');
  const [loginRole, setLoginRole] = useState<'admin' | 'client'>('admin');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginPortalType, setLoginPortalType] = useState<'gateway' | 'client' | 'internal'>('gateway');

  // Load and Subscribe to Databases in Real-Time for Multi-User Sync Integrity
  useEffect(() => {
    let active = true;
    setIsLoading(true);

    // Initial load and Firestore seed verification
    async function initAndSubscribe() {
      try {
        // Run gets once to ensure data is seeded if the Firestore collections are brand new/empty
        const clientsData = await dataService.getClients();
        const costCentersData = await dataService.getCostCenters();
        const goodsData = await dataService.getGoods();
        const foliosData = await dataService.getFolios();
        const preInvoicesData = await dataService.getPreInvoices();
        const meetingsData = await dataService.getMeetings();
        const calendarEventsData = await dataService.getCalendarEvents();

        if (active) {
          setClients(clientsData);
          setCostCenters(costCentersData);
          setGoods(goodsData);
          setFolios(foliosData);
          setPreInvoices(preInvoicesData);
          setMeetings(meetingsData);
          setCalendarEvents(calendarEventsData);

          // Seed reference variables for comparative notifications
          clientsRef.current = clientsData;
          costCentersRef.current = costCentersData;
          foliosRef.current = foliosData;
          preInvoicesRef.current = preInvoicesData;
          meetingsRef.current = meetingsData;
          calendarEventsRef.current = calendarEventsData;
          incidentsRef.current = await dataService.getIncidents();

          // Enable comparison checks after first initial download
          setIsFirstLoadFinished(true);
        }
      } catch (error) {
        console.error('Error during initial DB seeding/fetch:', error);
        if (active) {
          setIsFirstLoadFinished(true);
        }
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    }

    initAndSubscribe();

    // Set up real-time onSnapshot subscriptions for instant multi-user updates
    const unsubClients = dataService.subscribeToClients((items) => {
      if (active) {
        if (isFirstLoadFinishedRef.current) {
          items.forEach(newClient => {
            const oldClient = clientsRef.current.find(c => c.id === newClient.id);
            if (!oldClient) {
              const msg = `Se ha registrado al cliente "${newClient.name}" (${newClient.code}) en el sistema.`;
              NotificationService.sendNotification(`🏢 Nuevo Cliente Registrado`, { body: msg }, 'success');
            } else if (oldClient.name !== newClient.name || oldClient.contactEmail !== newClient.contactEmail) {
              const msg = `La información del cliente "${newClient.name}" ha sido modificada.`;
              NotificationService.sendNotification(`📝 Cliente Actualizado`, { body: msg }, 'info');
            }
          });
        }
        clientsRef.current = items;
        setClients(items);
      }
    });

    const unsubCostCenters = dataService.subscribeToCostCenters((items) => {
      if (active) {
        if (isFirstLoadFinishedRef.current) {
          items.forEach(newCC => {
            const oldCC = costCentersRef.current.find(cc => cc.id === newCC.id);
            if (!oldCC) {
              const msg = `Se ha dado de alta un nuevo Centro de Costos: "${newCC.name}" (${newCC.code}).`;
              NotificationService.sendNotification(`🏢 Centro de Costos Creado`, { body: msg }, 'success');
            }
          });
        }
        costCentersRef.current = items;
        setCostCenters(items);
      }
    });

    const unsubDestinations = dataService.subscribeToDestinations((items) => {
      if (active) {
        destinationsRef.current = items;
        setDestinations(items);
      }
    });

    const unsubGoods = dataService.subscribeToGoods((items) => {
      if (active) {
        setGoods(items);
      }
    });

    const unsubFolios = dataService.subscribeToFolios((items) => {
      if (active) {
        if (isFirstLoadFinishedRef.current) {
          items.forEach(newFolio => {
            const oldFolio = foliosRef.current.find(f => f.id === newFolio.id);
            if (!oldFolio) {
              const msg = `Se ha creado el folio logístico ${newFolio.id} para el cliente ${newFolio.clientName}.`;
              NotificationService.sendNotification(`📦 Nuevo Folio Creado: ${newFolio.id}`, { body: msg }, 'success');
              
              // Multi-device native push notification for folio creation
              fcmService.sendCriticalStatusNotification({
                title: `📦 Nuevo Folio Creado: ${newFolio.id}`,
                body: msg,
                folioId: newFolio.id,
                clientName: newFolio.clientName,
                newStatus: newFolio.status,
                category: 'GENERAL',
                url: '/#trazabilidad'
              }).catch(err => console.warn('[FCM Auto-Push Folio Create Error]:', err));

            } else if (oldFolio.status !== newFolio.status) {
              const stageLabels: Record<string, string> = {
                'RECOLECCION': 'Recolección en Origen',
                'ENTRADA_CEDIS': 'Entrada CEDIS y Recepción',
                'DECAPADO_TERMICO_QUIMICO': 'Decapado Térmico / Químico',
                'DECAPADO_LAVADO': 'Decapado y Lavado',
                'INSPECCION': 'Inspección de Calidad Final',
                'RETRABAJO': 'Retrabajo Técnico',
                'SALIDA_CEDIS': 'En Ruta de Entrega (Salida CEDIS)',
                'ENTREGA_PLANTA': 'Entregado en Planta',
                'DEVOLUCION': 'Devolución Registrada'
              };
              const label = stageLabels[newFolio.status] || newFolio.status;
              let msg = `El folio ${newFolio.id} (${newFolio.clientName}) avanzó a fase: ${label}.`;

              if (newFolio.status === 'SALIDA_CEDIS') {
                msg = `🚚 ¡EN RUTA DE ENTREGA! El camión con el folio ${newFolio.id} ha salido de CEDIS y se dirige a su planta.`;
              } else if (newFolio.status === 'ENTREGA_PLANTA') {
                msg = `🏁 ¡ENTREGADO EN PLANTA! El camión ha llegado a su destino y las piezas del folio ${newFolio.id} fueron entregadas.`;
              } else if (newFolio.status === 'RETRABAJO') {
                msg = `⚠️ ALERTA DE INCIDENCIA: El folio ${newFolio.id} entró a retrabajo por inspección de calidad no conforme.`;
              } else if (newFolio.status === 'DEVOLUCION') {
                msg = `🚨 ALERTA DEVOLUCIÓN: Se registró devolución o rechazo en planta para el folio ${newFolio.id}.`;
              } else if (newFolio.status === 'INSPECCION') {
                msg = `🔍 INSPECCIÓN TÉCNICA: El folio ${newFolio.id} ha ingresado a inspección visual y dimensional.`;
              } else if (newFolio.status === 'DECAPADO_TERMICO_QUIMICO' || newFolio.status === 'DECAPADO_LAVADO') {
                msg = `🔥 TRATAMIENTO: El folio ${newFolio.id} está en proceso de decapado pirolítico y tinas de neutralización.`;
              } else if (newFolio.status === 'ENTRADA_CEDIS') {
                msg = `🏢 RECEPCIÓN CEDIS: El folio ${newFolio.id} ha sido recibido y pesado en patio CEDIS.`;
              }

              // Local Web Notification + Dual Tone Sound
              NotificationService.sendNotification(
                `🚨 Fase Folio ${newFolio.id}: ${label}`,
                { body: msg },
                newFolio.status === 'RETRABAJO' || newFolio.status === 'DEVOLUCION' ? 'critical' :
                newFolio.status === 'ENTREGA_PLANTA' ? 'success' :
                newFolio.status === 'SALIDA_CEDIS' ? 'warning' : 'info'
              );

              // Multi-device Native Push Notification via Firebase FCM & Service Worker
              const category = 
                newFolio.status === 'RETRABAJO' ? 'RETRABAJO' :
                newFolio.status === 'DEVOLUCION' ? 'DEVOLUCION' :
                newFolio.status === 'ENTREGA_PLANTA' ? 'ENTREGA_PLANTA' :
                newFolio.status === 'SALIDA_CEDIS' ? 'URGENTE' : 'GENERAL';

              fcmService.sendCriticalStatusNotification({
                title: `🚨 Fase Operativa: ${label} (${newFolio.id})`,
                body: msg,
                folioId: newFolio.id,
                clientName: newFolio.clientName,
                newStatus: newFolio.status,
                category,
                url: '/#trazabilidad'
              }).catch(err => console.warn('[FCM Auto-Push Phase Transition Error]:', err));
            }
          });
        }
        foliosRef.current = items;
        setFolios(items);
      }
    });

    const unsubPreInvoices = dataService.subscribeToPreInvoices((items) => {
      if (active) {
        if (isFirstLoadFinishedRef.current) {
          items.forEach(newInv => {
            const oldInv = preInvoicesRef.current.find(inv => inv.id === newInv.id);
            if (!oldInv) {
              const msg = `Se ha generado una nueva prefactura ${newInv.id} por un monto de $${newInv.totalMXN.toLocaleString()} MXN.`;
              NotificationService.sendNotification(`📄 Nueva Prefactura Emitida`, { body: msg }, 'success');
              
              fcmService.sendCriticalStatusNotification({
                title: `📄 Nueva Prefactura Emitida: ${newInv.id}`,
                body: msg,
                category: 'PREFACTURA',
                clientName: newInv.clientName,
                url: '/#facturas_cobros'
              }).catch(err => console.warn('[FCM Auto-Push PreInvoice Error]:', err));

            } else if (oldInv.status !== newInv.status) {
              const msg = `La prefactura ${newInv.id} ha cambiado de estado a: "${newInv.status}".`;
              NotificationService.sendNotification(`📄 Prefactura Actualizada: ${newInv.status}`, { body: msg }, 'info');
              
              fcmService.sendCriticalStatusNotification({
                title: `📄 Prefactura ${newInv.id} Actualizada (${newInv.status})`,
                body: msg,
                category: 'PREFACTURA',
                clientName: newInv.clientName,
                url: '/#facturas_cobros'
              }).catch(err => console.warn('[FCM Auto-Push PreInvoice Status Error]:', err));
            }
          });
        }
        preInvoicesRef.current = items;
        setPreInvoices(items);
      }
    });

    const unsubMeetings = dataService.subscribeToMeetings((items) => {
      if (active) {
        if (isFirstLoadFinishedRef.current) {
          items.forEach(newMeet => {
            const oldMeet = meetingsRef.current.find(m => m.id === newMeet.id);
            if (!oldMeet) {
              const msg = `Nueva sesión de Meet agendada: "${newMeet.title}".`;
              NotificationService.sendNotification(`📹 Sesión Agendada`, { body: msg }, 'info');
            }
          });
        }
        meetingsRef.current = items;
        setMeetings(items);
      }
    });

    const unsubCalendarEvents = dataService.subscribeToCalendarEvents((items) => {
      if (active) {
        if (isFirstLoadFinishedRef.current) {
          items.forEach(newEvent => {
            const oldEvent = calendarEventsRef.current.find(e => e.id === newEvent.id);
            if (!oldEvent) {
              const msg = `Nuevo evento programado: "${newEvent.title}".`;
              NotificationService.sendNotification(`📅 Evento Programado`, { body: msg }, 'info');
            }
          });
        }
        calendarEventsRef.current = items;
        setCalendarEvents(items);
      }
    });

    const unsubIncidents = dataService.subscribeToIncidents((items) => {
      if (active) {
        if (isFirstLoadFinishedRef.current) {
          items.forEach(newInc => {
            const oldInc = incidentsRef.current.find(i => i.id === newInc.id);
            if (!oldInc) {
              const label = newInc.type === 'DEVOLUCION' ? 'Retrabajo Cliente' : newInc.type === 'SCRAP' ? 'Scrap / Pieza Dañada' : 'Retrabajo Técnico';
              NotificationService.sendNotification(`⚠️ ${label} Registrado`, { 
                body: `Incidencia registrada para el folio ${newInc.folioId} (${newInc.clientName}): ${newInc.causeCategory}.` 
              }, 'warning');
            }
          });
        }
        incidentsRef.current = items;
      }
    });

    const unsubUsers = dataService.subscribeToUsers((usersList) => {
      if (active) {
        // Force-sync logic comparing cached/current user state against Firestore database
        let savedUser: UserProfile | null = null;
        try {
          const savedUserJson = localStorage.getItem('snfsic_logged_user');
          if (savedUserJson) {
            savedUser = JSON.parse(savedUserJson);
          }
        } catch (e) {
          console.warn("Error parsing saved user from localStorage", e);
        }

        setCurrentUser(prevUser => {
          const targetUser = prevUser || savedUser;
          if (!targetUser || !targetUser.email) return null;

          const emailTarget = targetUser.email.trim().toLowerCase();
          const firestoreUser = usersList.find(u => u.email && u.email.trim().toLowerCase() === emailTarget);

          if (firestoreUser) {
            // Check for inconsistencies between cached/current user and Firestore
            const prevSerialized = JSON.stringify({
              role: targetUser.role,
              name: targetUser.name,
              permissions: targetUser.permissions,
              clientCode: targetUser.clientCode,
              password: targetUser.password
            });
            const freshSerialized = JSON.stringify({
              role: firestoreUser.role,
              name: firestoreUser.name,
              permissions: firestoreUser.permissions,
              clientCode: firestoreUser.clientCode,
              password: firestoreUser.password
            });

            const hasInconsistency = prevSerialized !== freshSerialized || !prevUser;

            if (hasInconsistency) {
              console.log(`[Force-Sync Users] Sincronización forzada de usuario (${emailTarget}): datos actualizados desde Firestore.`);
              try {
                localStorage.setItem('snfsic_logged_user', JSON.stringify(firestoreUser));
              } catch (e) {
                console.warn("Could not update snfsic_logged_user in localStorage", e);
              }
              setIsLoggedIn(true);
              return firestoreUser;
            }
            
            setIsLoggedIn(true);
            return prevUser || firestoreUser;
          }

          // If the logged in user is no longer found in Firestore usersList:
          if (usersList && usersList.length > 0) {
            console.warn(`[User Deleted] Logged user (${emailTarget}) was deleted from system.`);
            try {
              localStorage.removeItem('snfsic_logged_user');
            } catch (e) {
              console.warn("Could not remove snfsic_logged_user", e);
            }
            setIsLoggedIn(false);
            return null;
          }

          if (!prevUser && savedUser) {
            setIsLoggedIn(true);
            return savedUser;
          }

          return prevUser;
        });
      }
    });

    // Fetch USD to MXN exchange rate dynamically from backend
    refreshExchangeRate();

    return () => {
      active = false;
      unsubClients();
      unsubCostCenters();
      unsubGoods();
      unsubFolios();
      unsubPreInvoices();
      unsubMeetings();
      unsubCalendarEvents();
      unsubIncidents();
      unsubUsers();
    };
  }, []);

  // Auto-refresh exchange rate in real-time every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      refreshExchangeRate();
    }, 30000); // 30 seconds
    return () => clearInterval(interval);
  }, []);

  // Fetch exchange rate from Express API
  const refreshExchangeRate = async () => {
    try {
      const res = await fetch('/api/exchange-rate');
      if (!res.ok) {
        throw new Error(`Server returned status: ${res.status}`);
      }
      const data = await res.json();
      if (data && typeof data.rate === 'number') {
        setExchangeRate(data.rate);
        setLastUpdatedRate(data.lastUpdated || new Date().toISOString());
      }
    } catch (err) {
      console.warn('Note: Could not sync live FX from Express server, using default fallback rate.', err);
    }
  };

  const isMenuAllowed = (menuKey: string): boolean => {
    if (!currentUser) return false;
    
    // Super-admin override: ADMINISTRADOR gets access to everything
    if (isAdminRole(currentUser.role) || currentUser.role === 'admin') {
      return true;
    }

    // If explicit menuAccess mapping is set and contains non-empty permissions, check key!
    if (currentUser.permissions && currentUser.permissions.menuAccess && Object.keys(currentUser.permissions.menuAccess).length > 0) {
      if (currentUser.permissions.menuAccess[menuKey] !== undefined) {
        return !!currentUser.permissions.menuAccess[menuKey];
      }
    }

    // Sane defaults based on role types in case menuAccess is not set yet
    const role = currentUser.role;

    // General menus
    if (menuKey === 'welcome') return true;
    if (menuKey === 'kpis') return true;
    if (menuKey === 'calendar') return true;
    if (menuKey === 'meetings') return true;
    if (menuKey === 'dashboard') {
      return isInternalRole(role) && role !== 'CHOFER';
    }
    if (menuKey === 'admin_portal') {
      return isAdminRole(role);
    }
    if (menuKey === 'clients') {
      return isAdminRole(role) || role === 'GERENTE';
    }

    // Client Submenus default access
    if (menuKey.startsWith('client_')) {
      if (role === 'CHOFER' && menuKey === 'client_cargas_envios') return true;
      if (isClientUser(currentUser)) {
        if (isClientOperatorUser(currentUser) && menuKey === 'client_facturas_cobros') {
          return false;
        }
        return true;
      }
      if (isAdminRole(role) || role === 'GERENTE' || role === 'FACTURACION' || role === 'OPERACIONES' || role === 'CALIDAD') {
        return true;
      }
      return false; // Hidden unless authorized explicitly in menuAccess
    }

    // Internal Submenus default access
    if (menuKey.startsWith('internal_')) {
      if (isInternalRole(role) || !currentUser.clientCode) {
        if (role === 'CHOFER') {
          return menuKey === 'internal_seguridad' || menuKey === 'internal_operaciones';
        }
        return true;
      }
      return false;
    }

    return true;
  };

  const handleResetDatabase = async () => {
    setIsLoading(true);
    try {
      await dataService.clearAllDatabase();
      setClients([]);
      setCostCenters([]);
      setGoods([]);
      setFolios([]);
      setPreInvoices([]);
      setMeetings([]);
      setCalendarEvents([]);
      
      await handleLogActivity('VACIAR_BD', 'La base de datos fue limpiada por completo (datos de prueba eliminados).');
      await window.showAppAlert('La base de datos y el almacenamiento local han sido vaciados con éxito. La aplicación se encuentra en blanco.', 'Base de Datos Reiniciada');
    } catch (err) {
      console.error('Error resetting database:', err);
      await window.showAppAlert('Error al vaciar la base de datos: ' + err, 'Error de Operación');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogActivity = async (action: string, details: string, overrideEmail?: string) => {
    try {
      const email = overrideEmail || currentUser?.email || 'sistema';
      const newLog = {
        id: `log-${Math.random().toString(36).substr(2, 9)}`,
        timestamp: new Date().toISOString(),
        userEmail: email,
        action,
        details
      };
      await dataService.saveActivityLog(newLog);
    } catch (err) {
      console.error('Failed to log system activity:', err);
    }
  };

  // Auth logins handles
  const handleDemoLogin = async (roleType: 'admin' | 'client') => {
    const email = roleType === 'admin' ? 'usuario@grupoisn.com.mx' : 'usuario@grupoisn.com.mx';
    const usersList = await dataService.getUsers();
    let foundUser = usersList.find(u => u.email && u.email.toLowerCase() === email.toLowerCase());

    if (!foundUser && roleType === 'admin') {
      foundUser = usersList.find(u => isAdminRole(u.role));
    }

    if (foundUser) {
      setCurrentUser(foundUser);
      setActiveTab('welcome');
      setIsProfileDropdownOpen(false);
      setIsLoggedIn(true);
      setIsInternoFolderOpen(false);
      try {
        localStorage.setItem('snfsic_logged_user', JSON.stringify(foundUser));
      } catch (e) {
        console.warn('Could not save user session to localStorage', e);
      }
      await handleLogActivity('INICIO_SESION', `Sesión iniciada mediante acceso rápido demo (${foundUser.name}).`, email);
    } else {
      // Fallback in case firestore hasn't synced
      const defaultUser: UserProfile = roleType === 'admin' ? {
        uid: 'u-usuario-isn',
        email: 'usuario@grupoisn.com.mx',
        name: 'Usuario Administrador ISN',
        role: 'ADMINISTRADOR',
        password: 'demo1234',
        permissions: {
          canCreateFolios: true,
          canUpdateStages: true,
          canViewDashboard: true,
          canManageUsers: true,
          canBilling: true,
          menuAccess: {
            clientProcessTab: true,
            foliadorTab: true,
            catalogoBienesTab: true,
            costCentersTab: true,
            generalControlTab: true,
            adminTab: true,
            billingTab: true
          }
        }
      } : {
        uid: 'u-client',
        email: 'usuario@grupoisn.com.mx',
        name: 'Socio Comercial (Procesos de Maquinado)',
        role: 'OPERACION',
        password: 'demo1234',
        clientCode: 'CLI-PROMAQ',
        permissions: {
          canCreateFolios: false,
          canUpdateStages: false,
          canViewDashboard: false,
          canManageUsers: false,
          canBilling: false,
          menuAccess: {}
        }
      };
      setCurrentUser(defaultUser);
      setActiveTab('welcome');
      setIsProfileDropdownOpen(false);
      setIsLoggedIn(true);
      setIsInternoFolderOpen(false);
      try {
        localStorage.setItem('snfsic_logged_user', JSON.stringify(defaultUser));
      } catch (e) {
        console.warn('Could not save user session to localStorage', e);
      }
      await handleLogActivity('INICIO_SESION', `Sesión iniciada mediante fallback demo (${defaultUser.name}).`, email);
    }
  };

  const handleCustomLoginSubmit = async (e: React.FormEvent, portalType: 'client' | 'internal') => {
    e.preventDefault();
    if (!loginEmail) return;

    const emailTrim = loginEmail.trim().toLowerCase();
    let usersList = await dataService.getUsers();
    
    // Find matching user by email (case-insensitive & trimmed)
    let foundUser = usersList.find(u => u.email && u.email.trim().toLowerCase() === emailTrim);

    // Auto-create or fallback if logging in as usuario@grupoisn.com.mx
    if (!foundUser && (emailTrim === 'usuario@grupoisn.com.mx' || emailTrim === 'admin@snfsic.com')) {
      foundUser = {
        uid: 'u-usuario-isn',
        email: 'usuario@grupoisn.com.mx',
        name: 'Usuario Administrador ISN',
        role: 'ADMINISTRADOR',
        password: 'demo1234',
        permissions: {
          canCreateFolios: true,
          canUpdateStages: true,
          canViewDashboard: true,
          canManageUsers: true,
          canBilling: true,
          menuAccess: {
            clientProcessTab: true,
            foliadorTab: true,
            catalogoBienesTab: true,
            costCentersTab: true,
            generalControlTab: true,
            adminTab: true,
            billingTab: true
          }
        }
      };
    }

    // Auto-create or fallback if logging in as Victor Macias
    if (!foundUser && (emailTrim.includes('victor') && emailTrim.includes('macias') || emailTrim === 'vmacias@grupoisn.com.mx')) {
      foundUser = {
        uid: 'u-victor-macias',
        email: emailTrim,
        name: 'Víctor Macías',
        role: 'ADMINISTRADOR',
        password: 'demo1234',
        permissions: {
          canCreateFolios: true,
          canUpdateStages: true,
          canViewDashboard: true,
          canManageUsers: true,
          canBilling: true,
          menuAccess: {
            clientProcessTab: true,
            foliadorTab: true,
            catalogoBienesTab: true,
            costCentersTab: true,
            generalControlTab: true,
            adminTab: true,
            billingTab: true
          }
        }
      };
    }

    if (!foundUser) {
      await window.showAppAlert(`El correo '${emailTrim}' no se encuentra registrado en el sistema. Verifique sus credenciales o solicite acceso al Administrador.`, 'Usuario No Registrado');
      return;
    }

    // Password verification (if password is set on user record)
    const passInput = loginPassword ? loginPassword.trim() : '';
    const userPass = foundUser.password ? foundUser.password.trim() : '';

    if (userPass && passInput && passInput !== userPass && passInput !== 'demo1234') {
      await window.showAppAlert('La contraseña ingresada es incorrecta. Verifique sus credenciales.', 'Error de Autenticación');
      return;
    }

    // Determine user classification
    const isUserAdmin = isAdminRole(foundUser.role) || foundUser.role === 'admin';
    const isClient = isClientRole(foundUser.role) || !!foundUser.clientCode;
    const isInternal = isInternalRole(foundUser.role) || (!foundUser.clientCode && !isClient);

    // Validate portal access strictly yet flexibly (Administrators have unrestricted access to both portals)
    if (!isUserAdmin) {
      if (portalType === 'client' && !isClient && isInternalRole(foundUser.role)) {
        await window.showAppAlert('Acceso Incorrecto: Esta cuenta corresponde al Personal Interno (Staff). Por favor, ingrese desde el Portal de Personal Interno.', 'Portal Equivocado');
        return;
      }

      if (portalType === 'internal' && !isInternal && isClientRole(foundUser.role)) {
        await window.showAppAlert('Acceso Incorrecto: Esta cuenta corresponde a un Socio Comercial (Cliente). Por favor, ingrese desde el Portal de Socios Comerciales.', 'Portal Equivocado');
        return;
      }
    }

    // Save session in state and localStorage for persistence
    setCurrentUser(foundUser);
    setActiveTab('welcome');
    setIsProfileDropdownOpen(false);
    setIsLoggedIn(true);
    setIsInternoFolderOpen(false);
    try {
      localStorage.setItem('snfsic_logged_user', JSON.stringify(foundUser));
    } catch (err) {
      console.warn("Could not save user session to localStorage", err);
    }
    await handleLogActivity('INICIO_SESION', `Sesión iniciada con éxito (${foundUser.name}).`, foundUser.email);
  };

  // State update callback propagators to firestore & state
  const handleUpdateFolio = async (updatedFolio: Folio, previousFolioId?: string) => {
    try {
      const matchId = previousFolioId || updatedFolio.id;
      setFolios(prev => prev.map(f => f.id === matchId ? updatedFolio : f));
      if (previousFolioId && previousFolioId !== updatedFolio.id) {
        await dataService.deleteFolio(previousFolioId);
      }
      await dataService.saveFolio(updatedFolio);

      // Refresh PreInvoices in case one was automatically created at Entrega Planta state
      const preInvoicesData = await dataService.getPreInvoices();
      setPreInvoices(preInvoicesData);
      const calendarEventsData = await dataService.getCalendarEvents();
      setCalendarEvents(calendarEventsData);

      triggerNativeNotification('Folio Actualizado', `El Folio ${updatedFolio.id} ha cambiado a etapa: ${updatedFolio.status}.`);
      await handleLogActivity('ACTUALIZACION_FOLIO', `Folio ${updatedFolio.id} actualizado a etapa ${updatedFolio.status}.`);
    } catch (err) {
      console.warn("Offline fallback for folio update", err);
      triggerNativeNotification('Folio Guardado Localmente', `El Folio ${updatedFolio.id} se actualizó localmente de forma segura (24/7).`);
    }
  };

  const handleCreateFolio = async (newFolio: Folio) => {
    try {
      setFolios(prev => [newFolio, ...prev]);
      await dataService.saveFolio(newFolio);

      // Automatically calendarize the pickup event
      const newEvent: CalendarEvent = {
        id: `ev-${Math.random().toString(36).substr(2, 9)}`,
        title: `Recolección Folio ${newFolio.id}`,
        description: `Iniciado por operador para ${newFolio.clientName}`,
        date: new Date().toISOString().split('T')[0],
        type: 'pickup',
        refId: newFolio.id
      };
      setCalendarEvents(prev => [newEvent, ...prev]);
      await dataService.saveCalendarEvent(newEvent);

      triggerNativeNotification('Nuevo Folio Creado', `Se registró el Folio ${newFolio.id} para ${newFolio.clientName}.`);
      await handleLogActivity('CREACION_FOLIO', `Nuevo folio registrado: ${newFolio.id} para cliente ${newFolio.clientName}`);
    } catch (err) {
      console.warn("Offline fallback for folio creation", err);
      triggerNativeNotification('Folio Guardado Localmente', `El Folio ${newFolio.id} se registró localmente en caché (24/7).`);
    }
  };

  const handleDeleteFolio = async (id: string) => {
    try {
      setFolios(prev => prev.filter(f => f.id !== id));
      setCalendarEvents(prev => prev.filter(e => e.refId !== id && !e.title?.includes(id) && !e.description?.includes(id)));
      await dataService.deleteFolio(id);
      await handleLogActivity('ELIMINACION_FOLIO', `Folio eliminado: ${id}`);
      triggerNativeNotification('Folio Eliminado', `El Folio ${id} fue eliminado con éxito.`);
    } catch (err) {
      console.warn("Offline fallback for folio deletion", err);
      // Fallback local update
      setFolios(prev => prev.filter(f => f.id !== id));
      setCalendarEvents(prev => prev.filter(e => e.refId !== id && !e.title?.includes(id) && !e.description?.includes(id)));
    }
  };

  const handleAddClient = async (newClient: Client) => {
    setClients(prev => [...prev, newClient]);
    await dataService.saveClient(newClient);
    await handleLogActivity('CREACION_CLIENTE', `Nuevo cliente registrado: ${newClient.name} (${newClient.code})`);
  };

  const handleAddCostCenter = async (newCC: CostCenter) => {
    setCostCenters(prev => [...prev, newCC]);
    await dataService.saveCostCenter(newCC);
    const targetClient = clients.find(c => c.id === newCC.clientId)?.name || 'Cliente';
    await handleLogActivity('CREACION_CENTRO_COSTOS', `Centro de costos creado: ${newCC.name} (${newCC.code}) para ${targetClient}`);
  };

  const handleUpdateClient = async (updatedClient: Client) => {
    setClients(prev => prev.map(c => c.id === updatedClient.id ? updatedClient : c));
    await dataService.saveClient(updatedClient);
    await handleLogActivity('ACTUALIZACION_CLIENTE', `Cliente actualizado: ${updatedClient.name} (${updatedClient.code})`);
  };

  const handleDeleteClient = async (id: string) => {
    const clientToDelete = clients.find(c => c.id === id);
    if (!clientToDelete) return;
    setClients(prev => prev.filter(c => c.id !== id));
    await dataService.deleteClient(id);
    await handleLogActivity('ELIMINACION_CLIENTE', `Cliente eliminado: ${clientToDelete.name} (${clientToDelete.code})`);
  };

  const handleUpdateCostCenter = async (updatedCC: CostCenter) => {
    setCostCenters(prev => prev.map(cc => cc.id === updatedCC.id ? updatedCC : cc));
    await dataService.saveCostCenter(updatedCC);
    const targetClient = clients.find(c => c.id === updatedCC.clientId)?.name || 'Cliente';
    await handleLogActivity('ACTUALIZACION_CENTRO_COSTOS', `Centro de costos actualizado: ${updatedCC.name} (${updatedCC.code}) para ${targetClient}`);
  };

  const handleDeleteCostCenter = async (id: string) => {
    const ccToDelete = costCenters.find(cc => cc.id === id);
    if (!ccToDelete) return;
    setCostCenters(prev => prev.filter(cc => cc.id !== id));
    await dataService.deleteCostCenter(id);
    await handleLogActivity('ELIMINACION_CENTRO_COSTOS', `Centro de costos eliminado: ${ccToDelete.name} (${ccToDelete.code})`);
  };

  const handleAddDestination = async (newDest: Destination) => {
    setDestinations(prev => [...prev, newDest]);
    await dataService.saveDestination(newDest);
    await handleLogActivity('CREACION_DESTINO', `Nuevo destino de entrega registrado: ${newDest.name}`);
  };

  const handleUpdateDestination = async (updatedDest: Destination) => {
    setDestinations(prev => prev.map(d => d.id === updatedDest.id ? updatedDest : d));
    await dataService.saveDestination(updatedDest);
    await handleLogActivity('ACTUALIZACION_DESTINO', `Destino de entrega actualizado: ${updatedDest.name}`);
  };

  const handleDeleteDestination = async (id: string) => {
    const destToDelete = destinations.find(d => d.id === id);
    if (!destToDelete) return;
    setDestinations(prev => prev.filter(d => d.id !== id));
    await dataService.deleteDestination(id);
    await handleLogActivity('ELIMINACION_DESTINO', `Destino de entrega eliminado: ${destToDelete.name}`);
  };

  const handleAddGoodsItem = async (newItem: GoodsItem) => {
    setGoods(prev => [...prev, newItem]);
    await dataService.saveGoodsItem(newItem);
    await handleLogActivity('CREACION_MERCANCIA', `Mercancía registrada en catálogo: ${newItem.description} (${newItem.code})`);
  };

  const handleDeleteGoodsItem = async (id: string) => {
    setGoods(prev => prev.filter(g => g.id !== id));
    await dataService.deleteGoodsItem(id);
    await handleLogActivity('ELIMINACION_MERCANCIA', `Mercancía eliminada del catálogo: ID ${id}`);
  };

  const handleDeleteMultipleGoodsItems = async (ids: string[]) => {
    setGoods(prev => prev.filter(g => !ids.includes(g.id)));
    for (const id of ids) {
      await dataService.deleteGoodsItem(id);
    }
    await handleLogActivity('ELIMINACION_MERCANCIA', `Eliminadas múltiples mercancías del catálogo (${ids.length} ítems)`);
  };

  const handleDeleteAllGoodsItems = async () => {
    const currentGoods = [...goods];
    setGoods([]);
    for (const item of currentGoods) {
      await dataService.deleteGoodsItem(item.id);
    }
    await handleLogActivity('ELIMINACION_MERCANCIA', `Catálogo de mercancías vaciado por completo`);
  };

  const handleUpdateGoodsItem = async (updatedItem: GoodsItem) => {
    setGoods(prev => prev.map(g => g.id === updatedItem.id ? updatedItem : g));
    await dataService.saveGoodsItem(updatedItem);
    await handleLogActivity('ACTUALIZACION_MERCANCIA', `Mercancía actualizada: ${updatedItem.description} (${updatedItem.code})`);
  };

  const handleAddMeeting = async (newMeeting: MeetMeeting) => {
    setMeetings(prev => [newMeeting, ...prev]);
    await dataService.saveMeeting(newMeeting);

    // Refresh calendar events
    const calendarEventsData = await dataService.getCalendarEvents();
    setCalendarEvents(calendarEventsData);

    const isReq = newMeeting.status === 'Requested' || newMeeting.requestedByClient;
    const msg = isReq
      ? `El cliente ${newMeeting.clientName} ha solicitado una junta virtual: "${newMeeting.title}".`
      : `Reunión de Meet agendada: "${newMeeting.title}" con ${newMeeting.clientName}.`;
    
    NotificationService.sendNotification(
      isReq ? `🚨 Solicitud de Reunión Virtual` : `📹 Sesión de Meet Agendada`,
      { body: msg },
      isReq ? 'warning' : 'success'
    );

    await handleLogActivity('AGENDAR_JUNTA', `Reunión de Meet ${isReq ? 'solicitada' : 'agendada'}: ${newMeeting.title} con cliente ${newMeeting.clientName}`);
  };

  const handleUpdateMeeting = async (updatedMeeting: MeetMeeting) => {
    setMeetings(prev => prev.map(m => m.id === updatedMeeting.id ? updatedMeeting : m));
    await dataService.saveMeeting(updatedMeeting);
    
    NotificationService.sendNotification(
      `📹 Actualización de Junta`,
      { body: `Se actualizó la reunión "${updatedMeeting.title}" (Estado: ${updatedMeeting.status}).` },
      'info'
    );

    await handleLogActivity('ACTUALIZACION_JUNTA', `Reunión de Meet actualizada: ${updatedMeeting.title}`);
  };

  const handleDeleteMeeting = async (meetingId: string) => {
    const target = meetings.find(m => m.id === meetingId);
    setMeetings(prev => prev.filter(m => m.id !== meetingId));
    await dataService.deleteMeeting(meetingId);
    
    // Refresh calendar events
    const calendarEventsData = await dataService.getCalendarEvents();
    setCalendarEvents(calendarEventsData);

    if (target) {
      NotificationService.sendNotification(
        `🗑️ Reunión Eliminada`,
        { body: `Se eliminó la junta virtual "${target.title}".` },
        'info'
      );
    }

    await handleLogActivity('ELIMINAR_JUNTA', `Reunión de Meet eliminada ID: ${meetingId}`);
  };

  const handleAddCalendarEvent = async (newEvent: CalendarEvent) => {
    setCalendarEvents(prev => [newEvent, ...prev]);
    await dataService.saveCalendarEvent(newEvent);
  };

  const handleLogout = () => {
    try {
      localStorage.removeItem('snfsic_logged_user');
    } catch (e) {
      console.warn(e);
    }
    setIsProfileDropdownOpen(false);
    setIsMobileMenuOpen(false);
    setIsLoggedIn(false);
    setCurrentUser(null);
    setLoginPortalType('gateway');
    setLoginEmail('');
    setLoginPassword('');
  };

  const handleUpdateInvoice = async (updatedInvoice: PreInvoice) => {
    try {
      setPreInvoices(prev => prev.map(i => i.id === updatedInvoice.id ? updatedInvoice : i));
      await dataService.savePreInvoice(updatedInvoice);
      triggerNativeNotification('Prefactura Actualizada', `La Prefactura ${updatedInvoice.id} se actualizó a: ${updatedInvoice.status}.`);
      await handleLogActivity('ACTUALIZACION_FACTURA', `Prefactura ${updatedInvoice.id} actualizada a estado ${updatedInvoice.status}.`);
    } catch (err) {
      console.warn("Offline fallback for invoice update", err);
      triggerNativeNotification('Prefactura Guardada Localmente', `La Prefactura ${updatedInvoice.id} se actualizó en caché (24/7).`);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
        <div className="space-y-4 text-center">
          <RefreshCw className="h-10 w-10 text-brand-red animate-spin mx-auto" />
          <h2 className="text-sm font-bold tracking-tight text-gray-700 uppercase">Inicializando Base de Datos SNF - SIC...</h2>
          <p className="text-xs text-gray-400">Estableciendo sincronización en la nube con Firebase Firestore</p>
        </div>
      </div>
    );
  }

  if (publicTerminalParams || trackedFolioId) {
    const targetFolioId = publicTerminalParams?.folioId || trackedFolioId;
    const targetFase = publicTerminalParams?.terminalFase;
    const targetClient = publicTerminalParams?.clientId;

    return (
      <PublicTrackingPortal
        initialFolioId={targetFolioId}
        initialFaseNumber={targetFase}
        initialClientId={targetClient}
        folios={folios}
        clients={clients}
        costCenters={costCenters}
        destinations={destinations}
        currentUser={currentUser}
        onUpdateFolio={handleUpdateFolio}
        appLogoUrl={APP_LOGO_URL}
        isOnline={isOnline}
        onBackToApp={() => {
          setPublicTerminalParams(null);
          setTrackedFolioId(null);
          window.history.pushState({}, '', window.location.pathname);
        }}
      />
    );
  }

  if (!isLoggedIn || !currentUser) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col justify-between relative overflow-hidden font-sans select-none selection:bg-brand-red/30 selection:text-red-200">
        
        {/* Futuristic Grid and Neon Ambient Glow Backdrop */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#0f172a_1px,transparent_1px),linear-gradient(to_bottom,#0f172a_1px,transparent_1px)] bg-[size:3.5rem_3.5rem] opacity-35 pointer-events-none"></div>
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-brand-red/10 rounded-full blur-3xl pointer-events-none animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-brand-red/5 rounded-full blur-3xl pointer-events-none animate-pulse" style={{ animationDelay: '2s' }}></div>

        {/* Animated Cyber Scan Line */}
        <div className="absolute left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-brand-red/40 to-transparent top-0 animate-[bounce_10s_infinite] pointer-events-none"></div>

        {/* TOP STATUS BAR */}
        <header className="border-b border-slate-900 bg-slate-950/80 backdrop-blur-md px-6 py-3.5 flex items-center justify-between z-20 relative">
          <div className="flex items-center gap-3">
            <div className="overflow-hidden">
              <img 
                src={APP_LOGO_URL} 
                alt="Logo SIC PRO" 
                className="h-16 md:h-20 object-contain" 
                referrerPolicy="no-referrer"
              />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-mono font-bold tracking-widest text-slate-400">SIC PRO SNF</span>
                <span className="h-1.5 w-1.5 rounded-full bg-brand-red animate-ping"></span>
              </div>
              <p className="text-[9px] text-brand-red font-mono tracking-widest uppercase font-bold">SNF - SIC SYSTEM V4.9</p>
            </div>
          </div>

          <div className="flex items-center gap-6">
            <div className="hidden lg:flex items-center gap-4 text-[10px] font-mono text-slate-500 border-r border-slate-900 pr-5">
              <div className="flex items-center gap-1.5">
                <span className="h-1 w-1 bg-brand-red rounded-full"></span>
                <span>SECURE LINK: ACTIVE</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="h-1 w-1 bg-brand-red rounded-full"></span>
                <span>DB: CLOUD FIRESTORE</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="h-1 w-1 bg-brand-red rounded-full"></span>
                <span>ENC: AES-256-GCM</span>
              </div>
            </div>
            <div className="flex items-center gap-2 bg-slate-900/60 border border-slate-800/80 px-3 py-1 rounded-md shadow-inner">
              <span className="text-[9px] font-mono font-bold text-slate-400 mr-1.5 uppercase">SYSTEM CLOCK:</span>
              <FuturisticHeaderClock />
            </div>
          </div>
        </header>

        {/* CENTERED PORTALS ACCESS CONTAINER */}
        <main className="flex-1 max-w-3xl w-full mx-auto px-6 py-10 md:py-16 flex flex-col items-center justify-center z-10 relative">
          <div className="w-full flex items-center justify-center">
            <AnimatePresence mode="wait">
              
              {/* GATEWAY LANDING SELECTOR */}
              {loginPortalType === 'gateway' && (
                <motion.div
                  key="gateway"
                  initial={{ opacity: 0, scale: 0.98 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.98 }}
                  transition={{ duration: 0.3 }}
                  className="w-full space-y-6"
                >
                  {/* Central Presentation Title */}
                  <div className="text-center space-y-2 max-w-xl mx-auto">
                    <span className="px-3 py-1 bg-cyan-950/60 border border-cyan-800/40 text-cyan-400 font-mono text-[9px] uppercase font-bold rounded-full tracking-widest inline-block animate-pulse">
                      SISTEMA DE SEGURIDAD Y ACCESO
                    </span>
                    <h2 className="text-2xl md:text-3xl font-bold text-white tracking-tight font-display">
                      Canal de Acceso a Portales
                    </h2>
                    <p className="text-slate-400 text-xs md:text-sm">
                      Seleccione su portal asignado para ingresar al sistema.
                    </p>
                  </div>

                  {/* Portals Split Cards Container */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-2">
                    
                    {/* CLIENTS GATE CARD */}
                    <div 
                      onClick={() => {
                        setLoginPortalType('client');
                        setLoginEmail('');
                        setLoginPassword('');
                      }}
                      className="group bg-slate-900/60 border border-slate-800 hover:border-cyan-500/50 rounded-2xl p-6 space-y-5 transition-all hover:-translate-y-1 cursor-pointer flex flex-col justify-between relative overflow-hidden shadow-2xl hover:shadow-cyan-500/10"
                    >
                      <div className="absolute top-0 right-0 w-24 h-24 bg-cyan-500/5 rounded-full blur-xl group-hover:bg-cyan-500/15 transition-all"></div>
                      <div className="absolute top-0 left-0 w-12 h-1 bg-cyan-500/30 group-hover:bg-cyan-500 transition-all"></div>

                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div className="bg-cyan-950/80 border border-cyan-800/50 p-3 rounded-xl text-cyan-400 group-hover:scale-110 transition-transform shadow-inner">
                            <Users className="h-6 w-6" />
                          </div>
                          <span className="font-mono text-[9px] text-cyan-400/70 font-bold tracking-wider bg-cyan-950/40 px-2 py-0.5 rounded-full border border-cyan-800/30">CLIENT_SECURE</span>
                        </div>

                        <div className="space-y-1.5">
                          <h3 className="text-base font-bold text-white group-hover:text-cyan-400 transition-colors flex items-center gap-2">
                            Socio Comercial
                            <span className="text-[8px] bg-cyan-950 text-cyan-400 px-2 py-0.5 rounded font-mono font-bold border border-cyan-800/40">CLIENTES</span>
                          </h3>
                          <p className="text-xs text-slate-400 leading-relaxed">
                            Acceso dedicado para plantas industriales afiliadas, gerencias externas y clientes de Grupo ISN.
                          </p>
                        </div>
                      </div>

                      <button 
                        type="button"
                        className="w-full mt-2 bg-cyan-950/80 hover:bg-cyan-900 border border-cyan-700/60 text-cyan-300 group-hover:text-white font-bold py-2.5 rounded-xl text-xs transition-all flex items-center justify-center gap-2 cursor-pointer shadow-md"
                      >
                        <span>Ingresar a Portal Clientes</span>
                        <ChevronRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
                      </button>
                    </div>

                    {/* STAFF GATE CARD */}
                    <div 
                      onClick={() => {
                        setLoginPortalType('internal');
                        setLoginEmail('');
                        setLoginPassword('');
                      }}
                      className="group bg-slate-900/60 border border-slate-800 hover:border-rose-500/50 rounded-2xl p-6 space-y-5 transition-all hover:-translate-y-1 cursor-pointer flex flex-col justify-between relative overflow-hidden shadow-2xl hover:shadow-rose-500/10"
                    >
                      <div className="absolute top-0 right-0 w-24 h-24 bg-rose-500/5 rounded-full blur-xl group-hover:bg-rose-500/15 transition-all"></div>
                      <div className="absolute top-0 left-0 w-12 h-1 bg-rose-500/30 group-hover:bg-rose-500 transition-all"></div>

                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div className="bg-rose-950/80 border border-rose-800/50 p-3 rounded-xl text-rose-400 group-hover:scale-110 transition-transform shadow-inner">
                            <Shield className="h-6 w-6" />
                          </div>
                          <span className="font-mono text-[9px] text-rose-400/70 font-bold tracking-wider bg-rose-950/40 px-2 py-0.5 rounded-full border border-rose-800/30">STAFF_ROOT</span>
                        </div>

                        <div className="space-y-1.5">
                          <h3 className="text-base font-bold text-white group-hover:text-rose-400 transition-colors flex items-center gap-2">
                            Personal Interno
                            <span className="text-[8px] bg-rose-950 text-rose-400 px-2 py-0.5 rounded font-mono font-bold border border-rose-800/40">STAFF</span>
                          </h3>
                          <p className="text-xs text-slate-400 leading-relaxed">
                            Acceso seguro para operadores, supervisores logísticos, choferes y personal de administración general.
                          </p>
                        </div>
                      </div>

                      <button 
                        type="button"
                        className="w-full mt-2 bg-rose-950/80 hover:bg-rose-900 border border-rose-700/60 text-rose-300 group-hover:text-white font-bold py-2.5 rounded-xl text-xs transition-all flex items-center justify-center gap-2 cursor-pointer shadow-md"
                      >
                        <span>Ingresar a Portal Personal</span>
                        <ChevronRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
                      </button>
                    </div>

                  </div>
                </motion.div>
              )}

              {/* CLIENT LOGIN INTERFACE */}
              {loginPortalType === 'client' && (
                <motion.div
                  key="client_login"
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -15 }}
                  transition={{ duration: 0.25 }}
                  className="w-full max-w-md"
                >
                  <div className="bg-slate-900/90 border border-cyan-500/30 rounded-2xl p-6 md:p-8 space-y-5 relative shadow-2xl shadow-cyan-500/5">
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-cyan-500 to-transparent"></div>
                    
                    <div className="text-center space-y-1.5">
                      <span className="font-mono text-[8px] uppercase tracking-widest text-cyan-400 bg-cyan-950 px-2.5 py-0.5 rounded-full border border-cyan-800/60 font-bold animate-pulse inline-block">
                        SECURE CLIENT LINK
                      </span>
                      <h3 className="text-xl font-bold text-white tracking-tight">Portal de Clientes</h3>
                      <p className="text-xs text-slate-400 max-w-xs mx-auto">
                        Visualice el estatus de sus folios logísticos, prefacturas y catálogos de manera transparente.
                      </p>
                    </div>

                    {/* Custom login form */}
                    <form onSubmit={(e) => handleCustomLoginSubmit(e, 'client')} className="space-y-3.5" autoComplete="off">
                      <div className="space-y-1">
                        <label className="text-[9px] font-bold text-slate-400 uppercase tracking-wider font-mono">Correo de Cliente Autorizado</label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-2.5 h-3.5 w-3.5 text-cyan-500/40" />
                          <input 
                            type="email" 
                            required
                            autoComplete="off"
                            placeholder="usuario@grupoisn.com.mx"
                            value={loginEmail}
                            onChange={(e) => setLoginEmail(e.target.value)}
                            className="pl-9 pr-4 py-2 bg-slate-950 border border-slate-800 focus:border-cyan-500 rounded-xl w-full text-xs text-slate-200 placeholder-slate-700 focus:outline-hidden transition-all shadow-inner focus:shadow-cyan-500/5"
                          />
                        </div>
                      </div>

                      <div className="space-y-1">
                        <label className="text-[9px] font-bold text-slate-400 uppercase tracking-wider font-mono">Contraseña de Enlace</label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-2.5 h-3.5 w-3.5 text-cyan-500/40" />
                          <input 
                            type="password" 
                            required
                            autoComplete="new-password"
                            value={loginPassword}
                            onChange={(e) => setLoginPassword(e.target.value)}
                            placeholder="••••••••"
                            className="pl-9 pr-4 py-2 bg-slate-950 border border-slate-800 focus:border-cyan-500 rounded-xl w-full text-xs text-slate-200 placeholder-slate-700 focus:outline-hidden transition-all shadow-inner focus:shadow-cyan-500/5"
                          />
                        </div>
                      </div>

                      <button
                        type="submit"
                        className="w-full bg-cyan-600 hover:bg-cyan-500 text-white font-bold py-2 rounded-xl text-xs transition-all flex items-center justify-center gap-1.5 cursor-pointer shadow-lg shadow-cyan-600/10 active:scale-[0.99]"
                      >
                        <UserCheck className="h-3.5 w-3.5" /> Autenticar Socio Comercial
                      </button>
                    </form>

                    {/* Back button */}
                    <div className="pt-2 border-t border-slate-800/80 flex justify-center">
                      <button
                        type="button"
                        onClick={() => {
                          setLoginPortalType('gateway');
                          setLoginEmail('');
                          setLoginPassword('');
                        }}
                        className="text-[9px] font-mono text-slate-400 hover:text-cyan-400 transition-colors flex items-center gap-1 cursor-pointer hover:underline uppercase tracking-wider"
                      >
                        <span>← Volver al Selector de Portales</span>
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* INTERNAL STAFF LOGIN INTERFACE */}
              {loginPortalType === 'internal' && (
                <motion.div
                  key="internal_login"
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -15 }}
                  transition={{ duration: 0.25 }}
                  className="w-full max-w-md"
                >
                  <div className="bg-slate-900/90 border border-rose-500/30 rounded-2xl p-6 md:p-8 space-y-5 relative shadow-2xl shadow-rose-500/5">
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-rose-500 to-transparent"></div>
                    
                    <div className="text-center space-y-1.5">
                      <span className="font-mono text-[8px] uppercase tracking-widest text-rose-400 bg-rose-950 px-2.5 py-0.5 rounded-full border border-rose-800/60 font-bold animate-pulse inline-block">
                        SECURE STAFF LINK
                      </span>
                      <h3 className="text-xl font-bold text-white tracking-tight">Portal Personal Interno</h3>
                      <p className="text-xs text-slate-400 max-w-xs mx-auto">
                        Centro administrativo y de despacho. Autorice folios, gestione cotizaciones y configure el sistema.
                      </p>
                    </div>

                    {/* Custom login form */}
                    <form onSubmit={(e) => handleCustomLoginSubmit(e, 'internal')} className="space-y-3.5" autoComplete="off">
                      <div className="space-y-1">
                        <label className="text-[9px] font-bold text-slate-400 uppercase tracking-wider font-mono">Correo de Personal Autorizado</label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-2.5 h-3.5 w-3.5 text-rose-500/40" />
                          <input 
                            type="email" 
                            required
                            autoComplete="off"
                            placeholder="usuario@grupoisn.com.mx"
                            value={loginEmail}
                            onChange={(e) => setLoginEmail(e.target.value)}
                            className="pl-9 pr-4 py-2 bg-slate-950 border border-slate-800 focus:border-rose-500 rounded-xl w-full text-xs text-slate-200 placeholder-slate-700 focus:outline-hidden transition-all shadow-inner focus:shadow-rose-500/5"
                          />
                        </div>
                      </div>

                      <div className="space-y-1">
                        <label className="text-[9px] font-bold text-slate-400 uppercase tracking-wider font-mono">Contraseña de Operador</label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-2.5 h-3.5 w-3.5 text-rose-500/40" />
                          <input 
                            type="password" 
                            required
                            autoComplete="new-password"
                            value={loginPassword}
                            onChange={(e) => setLoginPassword(e.target.value)}
                            placeholder="••••••••"
                            className="pl-9 pr-4 py-2 bg-slate-950 border border-slate-800 focus:border-rose-500 rounded-xl w-full text-xs text-slate-200 placeholder-slate-700 focus:outline-hidden transition-all shadow-inner focus:shadow-rose-500/5"
                          />
                        </div>
                      </div>

                      <button
                        type="submit"
                        className="w-full bg-rose-600 hover:bg-rose-500 text-white font-bold py-2 rounded-xl text-xs transition-all flex items-center justify-center gap-1.5 cursor-pointer shadow-lg shadow-rose-600/10 active:scale-[0.99]"
                      >
                        <UserCheck className="h-3.5 w-3.5" /> Autenticar Staff de Operaciones
                      </button>
                    </form>

                    {/* Back button */}
                    <div className="pt-2 border-t border-slate-800/80 flex justify-center">
                      <button
                        type="button"
                        onClick={() => {
                          setLoginPortalType('gateway');
                          setLoginEmail('');
                          setLoginPassword('');
                        }}
                        className="text-[9px] font-mono text-slate-400 hover:text-rose-400 transition-colors flex items-center gap-1 cursor-pointer hover:underline uppercase tracking-wider"
                      >
                        <span>← Volver al Selector de Portales</span>
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}

            </AnimatePresence>
          </div>
        </main>

        {/* FOOTER */}
        <footer className="border-t border-slate-900 bg-slate-950/40 py-4 px-6 text-center text-[10px] font-mono text-slate-500 flex flex-col md:flex-row items-center justify-between gap-2 z-20 relative">
          <div>
            <span>© {new Date().getFullYear()} GRUPO ISN - SNF - SIC. TODOS LOS DERECHOS RESERVADOS.</span>
          </div>
          <div className="flex gap-4 items-center">
            <span className="text-slate-600">SISTEMA COMPATIBLE CON FIREBASE AUTH Y FIRESTORE CLOUD</span>
            <div className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-emerald-500 animate-ping"></span>
              <span className="text-slate-400 font-bold uppercase">NODES ONLINE</span>
            </div>
          </div>
        </footer>

      </div>
    );
  }

  // MAIN APPLICATION WORKSPACE (AUTHENTICATED)
  return (
    <div className="min-h-screen min-h-[100dvh] h-screen h-[100dvh] w-full max-w-full overflow-x-hidden overflow-y-hidden bg-brand-metal-soft flex flex-col md:flex-row font-sans antialiased relative">
      
      {/* MOBILE MINIMAL TOP HEADER */}
      <div className="md:hidden bg-brand-dark border-b border-brand-dark-hover px-3.5 py-2 pt-safe flex items-center justify-between shrink-0 z-30 shadow-xs">
        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="p-1.5 text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg transition-colors cursor-pointer shrink-0"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          <img 
            src={APP_LOGO_URL} 
            alt="Logo SIC PRO" 
            className="h-9 object-contain" 
            referrerPolicy="no-referrer"
          />
          <span className="text-xs font-bold text-white tracking-tight font-sans">SIC PRO SNF</span>
        </div>

        <div className="flex items-center gap-2">
          <div 
            className={`flex items-center gap-1 px-2 py-0.5 rounded-full font-mono text-[9px] font-bold ${
              isOnline ? 'bg-emerald-950/60 text-emerald-400 border border-emerald-500/30' : 'bg-amber-950/60 text-amber-400 border border-amber-500/30 animate-pulse'
            }`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${isOnline ? 'bg-emerald-400 animate-ping' : 'bg-amber-400'}`} />
            <span>{isOnline ? '24/7' : 'OFFLINE'}</span>
          </div>
        </div>
      </div>

      {/* Mobile menu backdrop */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-slate-950/70 backdrop-blur-xs z-40 md:hidden transition-opacity overscroll-contain"
          onClick={() => setIsMobileMenuOpen(false)}
          id="mobile-menu-backdrop"
        />
      )}
      
      {/* LEFT SIDEBAR: Fixed vertical panel with header logo, scrollable menu, and dark theme matching screenshot */}
      <nav 
        className={`fixed md:relative inset-y-0 left-0 z-50 w-[275px] md:w-[265px] h-screen h-[100dvh] pb-safe bg-[#242524] border-r border-[#3a3d3a] border-t-4 border-t-brand-red shrink-0 flex flex-col text-[11px] shadow-2xl transition-transform duration-300 ease-in-out overscroll-contain ${
          isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        }`}
        id="sidebar-nav-container"
      >
        
        {/* SIDEBAR HEADER: LOGO, BRAND TITLE & SEDE SELECTOR */}
        <div className="p-3.5 pb-3 border-b border-[#3a3d3a] bg-[#292b29] shrink-0 space-y-2.5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <img 
                src={APP_LOGO_URL} 
                alt="Logo SIC PRO" 
                className="h-12 md:h-14 lg:h-16 object-contain rounded-md transition-transform hover:scale-105" 
                referrerPolicy="no-referrer"
              />
              <div>
                <h1 className="text-xs font-display font-bold text-white tracking-tight flex items-center gap-1">
                  SIC PRO SNF
                  <span className="text-[7px] bg-brand-red text-white px-1 py-0.2 rounded-xs font-bold uppercase tracking-wider font-mono">
                    PRO
                  </span>
                </h1>
                <p className="text-[8.5px] text-[#9a9d9a] font-medium tracking-wide">Sistema Integral de...</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setIsMobileMenuOpen(false)}
              className="md:hidden p-1 rounded-md text-[#9a9d9a] hover:text-white hover:bg-[#3a3d3a] transition-colors cursor-pointer"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          
          {/* SEDE / OPERACIÓN Section */}
          <div className="space-y-1 relative pt-0.5">
            <span className="text-[8.5px] text-[#8c8f8c] font-bold uppercase tracking-wider px-0.5 block font-mono">SEDE / OPERACIÓN</span>
            
            {/* If NOT Admin: strictly restrict to allowed cost centers */}
            {!isAdminRole(currentUser?.role) && currentUser?.role !== 'admin' && currentUser?.role !== 'administrador' ? (
              (() => {
                const currentCc = selectedCostCenter || allowedCostCenters[0];

                if (allowedCostCenters.length === 0) {
                  return (
                    <div className="w-full bg-[#0c1527] border border-red-900/50 text-red-300 px-3 py-2 rounded-lg font-bold flex items-center justify-between text-[11px] shadow-sm tracking-wide">
                      <span className="truncate flex items-center gap-2">
                        <span className="text-brand-red text-xs">🔒</span>
                        <span className="font-extrabold uppercase">SIN SEDE ASIGNADA</span>
                      </span>
                      <span className="text-[9px] bg-red-950 text-red-400 px-1.5 py-0.5 rounded font-mono font-bold border border-red-800">
                        Restringido
                      </span>
                    </div>
                  );
                }

                if (allowedCostCenters.length === 1) {
                  return (
                    <div className="w-full bg-[#0c1527] border border-[#1e293b] text-white px-3 py-2 rounded-lg font-bold flex items-center justify-between text-[11px] shadow-sm tracking-wide">
                      <span className="truncate flex items-center gap-2">
                        <span className="text-brand-red text-xs">📍</span>
                        <span className="font-extrabold uppercase">
                          {currentCc ? currentCc.name.replace('Planta ', '').replace('Taller de ', '') : 'SEDE ASIGNADA'}
                        </span>
                      </span>
                      <span className="text-[9px] bg-slate-800 text-slate-300 px-1.5 py-0.5 rounded font-mono font-bold flex items-center gap-1 border border-slate-700">
                        {isChoferUser(currentUser) ? '🚚 Chofer' : '🔒 Asignada'}
                      </span>
                    </div>
                  );
                }

                return (
                  <div className="relative">
                    <button
                      onClick={() => setIsCostCenterDropdownOpen(!isCostCenterDropdownOpen)}
                      className="w-full bg-[#0c1527] border border-[#1e293b] text-white text-left px-3 py-2 rounded-lg font-bold flex items-center justify-between transition-all hover:bg-[#111c34] cursor-pointer text-[11px] shadow-sm tracking-wide"
                    >
                      <span className="truncate flex items-center gap-2">
                        <span className="text-brand-red text-xs">📍</span>
                        <span className="font-extrabold uppercase">
                          {currentCc ? currentCc.name.replace('Planta ', '').replace('Taller de ', '') : 'SELECCIONAR SEDE'}
                        </span>
                      </span>
                      <ChevronDown className={`h-3.5 w-3.5 text-slate-400 transition-transform ${isCostCenterDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {isCostCenterDropdownOpen && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-[#0c1527] border border-[#1e293b] rounded-lg shadow-2xl z-50 overflow-hidden py-1 text-white">
                        {allowedCostCenters.map((cc) => (
                          <button
                            key={cc.id}
                            onClick={() => {
                              setSelectedCostCenter(cc);
                              setSelectedCostCenterFilter(cc.id);
                              setActiveInternalCostCenterId(cc.id);
                              setIsCostCenterDropdownOpen(false);
                              setExpandedClientSidebarId(null);
                            }}
                            className={`w-full text-left px-3 py-1.5 hover:bg-[#18233c] text-slate-200 transition-all text-[10px] block truncate ${selectedCostCenter?.id === cc.id ? 'bg-[#1e2d4d] font-bold text-white' : ''}`}
                          >
                            📍 {cc.name} ({cc.code})
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()
            ) : (
              /* For Staff / Admin Users: full access to all cost centers and global overview */
              <>
                <button
                  onClick={() => setIsCostCenterDropdownOpen(!isCostCenterDropdownOpen)}
                  className="w-full bg-[#0c1527] border border-[#1e293b] text-white text-left px-3 py-2 rounded-lg font-bold flex items-center justify-between transition-all hover:bg-[#111c34] cursor-pointer text-[11px] shadow-sm tracking-wide"
                >
                  <span className="truncate flex items-center gap-2">
                    <span className="text-brand-red text-xs">📍</span>
                    <span className="font-extrabold uppercase">{selectedCostCenter ? selectedCostCenter.name.replace('Planta ', '').replace('Taller de ', '') : 'TODAS LAS SEDES / GLOBAL'}</span>
                  </span>
                  <ChevronDown className={`h-3.5 w-3.5 text-slate-400 transition-transform ${isCostCenterDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {isCostCenterDropdownOpen && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-[#0c1527] border border-[#1e293b] rounded-lg shadow-2xl z-50 overflow-hidden py-1 text-white">
                    <button
                      onClick={() => {
                        setSelectedCostCenter(null);
                        setSelectedCostCenterFilter(null);
                        setIsCostCenterDropdownOpen(false);
                        setExpandedClientSidebarId(null);
                      }}
                      className="w-full text-left px-3 py-1.5 hover:bg-[#18233c] text-slate-200 font-medium transition-all text-[10px] block truncate"
                    >
                      🌐 Corporativo Global (Todo)
                    </button>
                    {costCenters.map((cc) => (
                      <button
                        key={cc.id}
                        onClick={() => {
                          setSelectedCostCenter(cc);
                          setSelectedCostCenterFilter(cc.id);
                          setActiveInternalCostCenterId(cc.id);
                          setIsCostCenterDropdownOpen(false);
                          setExpandedClientSidebarId(null);
                        }}
                        className={`w-full text-left px-3 py-1.5 hover:bg-[#18233c] text-slate-200 transition-all text-[10px] block truncate ${selectedCostCenter?.id === cc.id ? 'bg-[#1e2d4d] font-bold text-white' : ''}`}
                      >
                        📍 {cc.name} ({cc.code})
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* SIDEBAR SCROLLABLE MENU BODY WITH INDEPENDENT SCROLLBAR */}
        <div className="flex-1 overflow-y-auto min-h-0 p-3 space-y-3 custom-scrollbar text-[#d0d3d0]">
          
          {/* Standalone Home / Bienvenida Tab */}
          {isMenuAllowed('welcome') && (
            <button
              onClick={() => {
                setActiveTab('welcome');
                setExpandedClientSidebarId(null);
                setActiveInternalSubmenu(null);
              }}
              className={`w-full text-left px-2.5 py-1.5 rounded-md text-[11px] font-bold transition-all flex items-center gap-2 cursor-pointer ${
                activeTab === 'welcome'
                  ? 'bg-[#3a3d3a] text-white font-bold border-l-2 border-brand-red shadow-xs pl-2'
                  : 'text-[#d0d3d0] hover:bg-[#323432] hover:text-white'
              }`}
            >
              <Home className="h-4 w-4 text-white shrink-0" />
              <span>Inicio / Bienvenida</span>
            </button>
          )}

          {/* KPIS Menu Button (Accessible to internal staff with permission check; clients view it directly inside their client portal) */}
          {!isClientUser(currentUser) && isMenuAllowed('kpis') && (
            <button
              onClick={() => {
                setActiveTab('kpis');
                setExpandedClientSidebarId(null);
                setActiveInternalSubmenu(null);
              }}
              className={`w-full text-left px-2.5 py-1.5 rounded-md text-[11px] font-bold transition-all flex items-center justify-between gap-2 cursor-pointer ${
                activeTab === 'kpis'
                  ? 'bg-brand-red text-white font-bold shadow-xs'
                  : 'text-[#d0d3d0] hover:bg-[#323432] hover:text-white'
              }`}
              id="sidebar-menu-kpis"
            >
              <span className="flex items-center gap-2 truncate">
                <BarChart3 className="h-4 w-4 text-emerald-400 shrink-0" />
                <span className="truncate">KPIS Globales</span>
              </span>
              <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                100%
              </span>
            </button>
          )}

          {/* Dedicated Chofer Quick Navigation Button */}
          {currentUser?.role === 'CHOFER' && (
            <button
              onClick={() => {
                setActiveTab('internal_submenu');
                setActiveInternalSubmenu('operaciones');
                setExpandedClientSidebarId(null);
              }}
              className={`w-full text-left px-3 py-2.5 rounded-xl text-xs font-black transition-all flex items-center gap-2 cursor-pointer ${
                activeTab === 'internal_submenu' && activeInternalSubmenu === 'operaciones'
                  ? 'bg-brand-red text-white shadow-md border border-red-500'
                  : 'bg-[#323432] text-amber-300 hover:bg-[#3a3d3a] hover:text-white border border-amber-500/30'
              }`}
            >
              <Truck className="h-4 w-4 text-amber-400 shrink-0" />
              <span className="truncate">🚚 Mis Cargas y Envíos (Chofer)</span>
            </button>
          )}
            
            {/* FOLDER A: CLIENTES (Only rendered if there are clients associated with the selected cost center/view) */}
            {availableClientsForSidebar.length > 0 && [
              'client_dashboard', 'client_catalog', 'client_cargas_envios', 'client_trazabilidad',
              'client_facturas_cobros', 'client_historial', 'client_reprocesos', 'client_kpis',
              'client_alertas', 'client_chat_soporte'
            ].some(k => isMenuAllowed(k)) && (
              <div className="space-y-1">
                <button
                  onClick={() => setIsClientesFolderOpen(!isClientesFolderOpen)}
                  className="w-full text-left px-1 py-1 text-[#8c8f8c] font-bold uppercase tracking-wider flex items-center justify-between hover:text-white transition-all cursor-pointer text-[9px] font-mono"
                >
                  <span className="flex items-center gap-1.5">
                    {isClientesFolderOpen ? <FolderOpen className="h-3.5 w-3.5 text-[#8c8f8c]" /> : <Folder className="h-3.5 w-3.5 text-[#8c8f8c]" />}
                    CLIENTES ({availableClientsForSidebar.length})
                  </span>
                  {isClientesFolderOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                </button>

                {isClientesFolderOpen && (
                  <div className="pl-1 space-y-2 ml-1 mt-1">
                    {availableClientsForSidebar.map((client) => {
                      const isClientExpanded = expandedClientSidebarId === client.id;
                      const isClientActive = expandedClientSidebarId === client.id;
                      
                      return (
                        <div key={client.id} className="space-y-1">
                          
                          {/* Client Level Selector (Card Button - collapsed by default) */}
                          <button
                            onClick={() => {
                              if (isClientExpanded) {
                                // Collapse if already open
                                setExpandedClientSidebarId(null);
                              } else {
                                // Expand this client
                                setExpandedClientSidebarId(client.id);
                                setActiveTab('client_submenu');
                                const clientKeys = [
                                  { k: 'client_dashboard', sub: 'dashboard' },
                                  { k: 'client_catalog', sub: 'catalog' },
                                  { k: 'client_cargas_envios', sub: 'cargas_envios' },
                                  { k: 'client_trazabilidad', sub: 'trazabilidad' },
                                  { k: 'client_facturas_cobros', sub: 'facturas_cobros' },
                                  { k: 'client_historial', sub: 'historial' },
                                  { k: 'client_reprocesos', sub: 'reprocesos' },
                                  { k: 'client_kpis', sub: 'kpis' },
                                  { k: 'client_alertas', sub: 'alertas' },
                                  { k: 'client_chat_soporte', sub: 'chat_soporte' }
                                ];
                                const firstAllowed = clientKeys.find(item => isMenuAllowed(item.k));
                                setActiveClientSubmenu(firstAllowed ? firstAllowed.sub : 'dashboard');
                                setActiveInternalSubmenu(null);
                              }
                            }}
                            className={`w-full text-left px-3 py-2 rounded-xl font-extrabold transition-all flex items-center justify-between cursor-pointer ${
                              isClientActive && activeTab === 'client_submenu'
                                ? 'bg-[#363836] text-white border-2 border-white shadow-md'
                                : 'bg-[#484a48] text-[#e0e0e0] hover:bg-[#525552] border border-transparent hover:border-white/30'
                            }`}
                          >
                            <span className="truncate flex items-center gap-2">
                              <span className="bg-slate-700/60 p-1 rounded">🏢</span>
                              <span className="tracking-wide text-xs">{client.name}</span>
                            </span>
                            {isClientExpanded ? <ChevronDown className="h-3.5 w-3.5 text-white/80" /> : <ChevronRight className="h-3.5 w-3.5 text-white/80" />}
                          </button>

                          {/* Client Submenus (11 requested submenus) */}
                          {isClientExpanded && (
                            <div className="pl-3 space-y-0.5 border-l border-[#484a48] ml-3.5 mt-1">
                              
                              {/* 1. Tablero de Control */}
                              {isMenuAllowed('client_dashboard') && (
                                <button
                                  onClick={() => {
                                    setExpandedClientSidebarId(client.id);
                                    setActiveTab('client_submenu');
                                    setActiveClientSubmenu('dashboard');
                                    setActiveInternalSubmenu(null);
                                  }}
                                  className={`w-full text-left px-2 py-1 rounded-lg text-[10px] font-semibold transition-all flex items-center gap-1.5 cursor-pointer ${
                                    activeTab === 'client_submenu' && activeClientSubmenu === 'dashboard' && expandedClientSidebarId === client.id
                                      ? 'bg-brand-red text-white font-bold pl-2 shadow-xs'
                                      : 'text-[#c2c5c2] hover:text-white hover:bg-[#343634]'
                                  }`}
                                >
                                  <LayoutDashboard className="h-3 w-3 text-[#9a9d9a] shrink-0" />
                                  <span className="truncate">Tablero de Control</span>
                                </button>
                              )}

                              {/* 2. Catálogo de Mercancías */}
                              {isMenuAllowed('client_catalog') && (
                                <button
                                  onClick={() => {
                                    setExpandedClientSidebarId(client.id);
                                    setActiveTab('client_submenu');
                                    setActiveClientSubmenu('catalog');
                                    setActiveInternalSubmenu(null);
                                  }}
                                  className={`w-full text-left px-2 py-1 rounded-lg text-[10px] font-semibold transition-all flex items-center gap-1.5 cursor-pointer ${
                                    activeTab === 'client_submenu' && activeClientSubmenu === 'catalog' && expandedClientSidebarId === client.id
                                      ? 'bg-brand-red text-white font-bold pl-2 shadow-xs'
                                      : 'text-[#c2c5c2] hover:text-white hover:bg-[#343634]'
                                  }`}
                                >
                                  <Package className="h-3 w-3 text-[#9a9d9a] shrink-0" />
                                  <span className="truncate">Catálogo de Mercancías</span>
                                </button>
                              )}

                              {/* 3. Mis Cargas y Envíos */}
                              {isMenuAllowed('client_cargas_envios') && (
                                <button
                                  onClick={() => {
                                    setExpandedClientSidebarId(client.id);
                                    setActiveTab('client_submenu');
                                    setActiveClientSubmenu('cargas_envios');
                                    setActiveInternalSubmenu(null);
                                  }}
                                  className={`w-full text-left px-2 py-1 rounded-lg text-[10px] font-semibold transition-all flex items-center gap-1.5 cursor-pointer ${
                                    activeTab === 'client_submenu' && activeClientSubmenu === 'cargas_envios' && expandedClientSidebarId === client.id
                                      ? 'bg-brand-red text-white font-bold pl-2 shadow-xs'
                                      : 'text-[#c2c5c2] hover:text-white hover:bg-[#343634]'
                                  }`}
                                >
                                  <Truck className="h-3 w-3 text-[#9a9d9a] shrink-0" />
                                  <span className="truncate">Mis Cargas y Envíos</span>
                                </button>
                              )}

                              {/* 4. Trazabilidad de Folios */}
                              {isMenuAllowed('client_trazabilidad') && (
                                <button
                                  onClick={() => {
                                    setExpandedClientSidebarId(client.id);
                                    setActiveTab('client_submenu');
                                    setActiveClientSubmenu('trazabilidad');
                                    setActiveInternalSubmenu(null);
                                  }}
                                  className={`w-full text-left px-2 py-1 rounded-lg text-[10px] font-semibold transition-all flex items-center gap-1.5 cursor-pointer ${
                                    activeTab === 'client_submenu' && activeClientSubmenu === 'trazabilidad' && expandedClientSidebarId === client.id
                                      ? 'bg-brand-red text-white font-bold pl-2 shadow-xs'
                                      : 'text-[#c2c5c2] hover:text-white hover:bg-[#343634]'
                                  }`}
                                >
                                  <Compass className="h-3 w-3 text-[#9a9d9a] shrink-0" />
                                  <span className="truncate">Trazabilidad de Folios</span>
                                </button>
                              )}

                              {/* 5. Mis Facturas / Cobros */}
                              {isMenuAllowed('client_facturas_cobros') && (
                                <button
                                  onClick={() => {
                                    setExpandedClientSidebarId(client.id);
                                    setActiveTab('client_submenu');
                                    setActiveClientSubmenu('facturas_cobros');
                                    setActiveInternalSubmenu(null);
                                  }}
                                  className={`w-full text-left px-2 py-1 rounded-lg text-[10px] font-semibold transition-all flex items-center gap-1.5 cursor-pointer ${
                                    activeTab === 'client_submenu' && activeClientSubmenu === 'facturas_cobros' && expandedClientSidebarId === client.id
                                      ? 'bg-brand-red text-white font-bold pl-2 shadow-xs'
                                      : 'text-[#c2c5c2] hover:text-white hover:bg-[#343634]'
                                  }`}
                                >
                                  <FileText className="h-3 w-3 text-[#9a9d9a] shrink-0" />
                                  <span className="truncate">Mis Facturas / Cobros</span>
                                </button>
                              )}

                              {/* 6. Historial de Folios */}
                              {isMenuAllowed('client_historial') && (
                                <button
                                  onClick={() => {
                                    setExpandedClientSidebarId(client.id);
                                    setActiveTab('client_submenu');
                                    setActiveClientSubmenu('historial');
                                    setActiveInternalSubmenu(null);
                                  }}
                                  className={`w-full text-left px-2 py-1 rounded-lg text-[10px] font-semibold transition-all flex items-center gap-1.5 cursor-pointer ${
                                    activeTab === 'client_submenu' && activeClientSubmenu === 'historial' && expandedClientSidebarId === client.id
                                      ? 'bg-brand-red text-white font-bold pl-2 shadow-xs'
                                      : 'text-[#c2c5c2] hover:text-white hover:bg-[#343634]'
                                  }`}
                                >
                                  <History className="h-3 w-3 text-[#9a9d9a] shrink-0" />
                                  <span className="truncate">Historial de Folios</span>
                                </button>
                              )}

                              {/* 7. Reprocesos / Devoluciones */}
                              {isMenuAllowed('client_reprocesos') && (
                                <button
                                  onClick={() => {
                                    setExpandedClientSidebarId(client.id);
                                    setActiveTab('client_submenu');
                                    setActiveClientSubmenu('reprocesos');
                                    setActiveInternalSubmenu(null);
                                  }}
                                  className={`w-full text-left px-2 py-1 rounded-lg text-[10px] font-semibold transition-all flex items-center gap-1.5 cursor-pointer ${
                                    activeTab === 'client_submenu' && activeClientSubmenu === 'reprocesos' && expandedClientSidebarId === client.id
                                      ? 'bg-brand-red text-white font-bold pl-2 shadow-xs'
                                      : 'text-[#c2c5c2] hover:text-white hover:bg-[#343634]'
                                  }`}
                                >
                                  <AlertOctagon className="h-3 w-3 text-[#9a9d9a] shrink-0" />
                                  <span className="truncate">{isClientUser(currentUser) ? 'Retrabajos' : 'Reprocesos / Retrabajos'}</span>
                                </button>
                              )}

                              {/* KPIS & Eficiencia (100% Meta Target) */}
                              {(isMenuAllowed('client_kpis') || isMenuAllowed('kpis')) && (
                                <button
                                  onClick={() => {
                                    setExpandedClientSidebarId(client.id);
                                    setActiveTab('client_submenu');
                                    setActiveClientSubmenu('kpis');
                                    setActiveInternalSubmenu(null);
                                  }}
                                  className={`w-full text-left px-2 py-1 rounded-lg text-[10px] font-semibold transition-all flex items-center justify-between gap-1 cursor-pointer ${
                                    activeTab === 'client_submenu' && activeClientSubmenu === 'kpis' && expandedClientSidebarId === client.id
                                      ? 'bg-brand-red text-white font-bold pl-2 shadow-xs'
                                      : 'text-[#c2c5c2] hover:text-white hover:bg-[#343634]'
                                  }`}
                                >
                                  <span className="truncate flex items-center gap-1.5">
                                    <BarChart3 className="h-3 w-3 text-emerald-400 shrink-0" />
                                    <span>KPIS</span>
                                  </span>
                                  <span className="text-[8px] font-mono px-1 py-0.2 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                                    100%
                                  </span>
                                </button>
                              )}

                              {/* Alertas y Avisos */}
                              {isMenuAllowed('client_alertas') && (
                                <button
                                  onClick={() => {
                                    setExpandedClientSidebarId(client.id);
                                    setActiveTab('client_submenu');
                                    setActiveClientSubmenu('alertas');
                                    setActiveInternalSubmenu(null);
                                  }}
                                  className={`w-full text-left px-2 py-1 rounded-lg text-[10px] font-semibold transition-all flex items-center justify-between gap-1 cursor-pointer ${
                                    activeTab === 'client_submenu' && activeClientSubmenu === 'alertas' && expandedClientSidebarId === client.id
                                      ? 'bg-brand-red text-white font-bold pl-2 shadow-xs'
                                      : 'text-[#c2c5c2] hover:text-white hover:bg-[#343634]'
                                  }`}
                                >
                                  <span className="truncate flex items-center gap-1.5">
                                    <Bell className="h-3 w-3 text-[#9a9d9a] shrink-0" />
                                    <span>Alertas y Avisos</span>
                                  </span>
                                  
                                  {/* Alarm count badge */}
                                  {folios.filter(f => f.clientId === client.id && f.status === 'RETRABAJO').length > 0 && (
                                    <span className="bg-brand-red text-white text-[8px] px-1 py-0.2 rounded-full font-bold">
                                      {folios.filter(f => f.clientId === client.id && f.status === 'RETRABAJO').length}
                                    </span>
                                  )}
                                </button>
                              )}

                              {/* 12. Chat de Soporte / WhatsApp */}
                              {isMenuAllowed('client_chat_soporte') && (
                                <button
                                  onClick={() => {
                                    setExpandedClientSidebarId(client.id);
                                    setActiveTab('client_submenu');
                                    setActiveClientSubmenu('chat_soporte');
                                    setActiveInternalSubmenu(null);
                                  }}
                                  className={`w-full text-left px-2 py-1 rounded-lg text-[10px] font-semibold transition-all flex items-center justify-between gap-1 cursor-pointer ${
                                    activeTab === 'client_submenu' && activeClientSubmenu === 'chat_soporte' && expandedClientSidebarId === client.id
                                      ? 'bg-brand-red text-white font-bold pl-2 shadow-xs'
                                      : 'text-[#c2c5c2] hover:text-white hover:bg-[#343634]'
                                  }`}
                                >
                                  <span className="truncate flex items-center gap-1.5">
                                    <MessageSquare className="h-3 w-3 text-[#9a9d9a] shrink-0" />
                                    <span>Soporte WhatsApp</span>
                                  </span>
                                  
                                  <span className="bg-emerald-900/80 text-emerald-300 text-[8px] px-1.5 py-0.2 rounded font-black border border-emerald-700">
                                    CHAT
                                  </span>
                                </button>
                              )}

                            </div>
                          )}

                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* FOLDER B: INTERNO (DIRECTLY FOR THE CURRENTLY SELECTED COST CENTER) */}
            {[
              'internal_proyectos', 'internal_seguridad', 'internal_operaciones', 'internal_rrhh',
              'internal_almacen', 'internal_activos', 'internal_mantenimiento', 'internal_it_sistemas'
            ].some(k => isMenuAllowed(k)) && (
              <div className="space-y-1">
                <button
                  onClick={() => setIsInternoFolderOpen(!isInternoFolderOpen)}
                  className="w-full text-left px-1 py-1 text-[#8c8f8c] font-bold uppercase tracking-wider flex items-center justify-between hover:text-white transition-all cursor-pointer text-[9px] font-mono"
                >
                  <span className="flex items-center gap-1.5 truncate">
                    {isInternoFolderOpen ? <FolderOpen className="h-3.5 w-3.5 text-[#8c8f8c]" /> : <Folder className="h-3.5 w-3.5 text-[#8c8f8c]" />}
                    <span className="truncate">
                      INTERNO {activeInternalCostCenter ? `• ${activeInternalCostCenter.code?.replace('CC-','') || activeInternalCostCenter.name}` : ''}
                    </span>
                  </span>
                  {isInternoFolderOpen ? <ChevronDown className="h-3 w-3 shrink-0" /> : <ChevronRight className="h-3 w-3 shrink-0" />}
                </button>

                {isInternoFolderOpen && (
                  <div className="pl-2 space-y-0.5 border-l border-[#484a48] ml-2 mt-1">
                    
                    {/* 1. Proyectos */}
                    {isMenuAllowed('internal_proyectos') && (
                      <div className="space-y-0.5">
                        <div className="flex items-center justify-between">
                          <button
                            onClick={() => {
                              setActiveTab('internal_submenu');
                              setActiveInternalSubmenu('proyectos');
                              setExpandedClientSidebarId(null);
                            }}
                            className={`flex-1 text-left px-2 py-1.5 rounded-lg text-[10px] font-semibold transition-all flex items-center gap-2 cursor-pointer ${
                              activeTab === 'internal_submenu' && activeInternalSubmenu === 'proyectos'
                                ? 'bg-brand-red text-white font-bold pl-2 shadow-xs'
                                : 'text-[#c2c5c2] hover:text-white hover:bg-[#343634]'
                            }`}
                          >
                            <span className="text-xs">📁</span>
                            <span className="truncate">Proyectos</span>
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setIsProjectsSubmenuOpen(!isProjectsSubmenuOpen);
                            }}
                            className="p-1 text-slate-400 hover:text-white rounded transition-colors"
                            title="Ver submenús de proyectos"
                          >
                            <ChevronDown className={`h-3 w-3 transition-transform ${isProjectsSubmenuOpen ? 'rotate-180' : ''}`} />
                          </button>
                        </div>

                        {isProjectsSubmenuOpen && (
                          <div className="pl-3 space-y-0.5 border-l border-[#3d403d] ml-2">
                            <button
                              onClick={() => {
                                setActiveTab('internal_submenu');
                                setActiveInternalSubmenu('proyectos');
                                setActiveProjectsSubtab('list');
                                setExpandedClientSidebarId(null);
                              }}
                              className={`w-full text-left px-2 py-1 rounded text-[9px] font-medium transition-all flex items-center gap-1.5 cursor-pointer ${
                                activeTab === 'internal_submenu' && activeInternalSubmenu === 'proyectos' && activeProjectsSubtab === 'list'
                                  ? 'text-red-400 font-bold bg-white/5'
                                  : 'text-[#a0a3a0] hover:text-white'
                              }`}
                            >
                              <span>📁</span>
                              <span className="truncate">Lista de Proyectos</span>
                            </button>
                            <button
                              onClick={() => {
                                setActiveTab('internal_submenu');
                                setActiveInternalSubmenu('proyectos');
                                setActiveProjectsSubtab('workplan');
                                setExpandedClientSidebarId(null);
                              }}
                              className={`w-full text-left px-2 py-1 rounded text-[9px] font-medium transition-all flex items-center gap-1.5 cursor-pointer ${
                                activeTab === 'internal_submenu' && activeInternalSubmenu === 'proyectos' && activeProjectsSubtab === 'workplan'
                                  ? 'text-red-400 font-bold bg-white/5'
                                  : 'text-[#a0a3a0] hover:text-white'
                              }`}
                            >
                              <span>📋</span>
                              <span className="truncate">Plan de trabajo</span>
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    {/* 2. Seguridad */}
                    {isMenuAllowed('internal_seguridad') && (
                      <button
                        onClick={() => {
                          setActiveTab('internal_submenu');
                          setActiveInternalSubmenu('seguridad');
                          setExpandedClientSidebarId(null);
                        }}
                        className={`w-full text-left px-2 py-1.5 rounded-lg text-[10px] font-semibold transition-all flex items-center justify-between gap-1.5 cursor-pointer ${
                          activeTab === 'internal_submenu' && activeInternalSubmenu === 'seguridad'
                            ? 'bg-brand-red text-white font-bold pl-2 shadow-xs'
                            : 'text-[#c2c5c2] hover:text-white hover:bg-[#343634]'
                        }`}
                      >
                        <div className="flex items-center gap-2 truncate">
                          <span className="text-xs">🛡️</span>
                          <span className="truncate">Seguridad</span>
                        </div>

                        {/* Visual Alert Badge for Certifications <30 days */}
                        {certAlertEvaluation.hasUrgentAlerts && (
                          <span 
                            className={`px-1.5 py-0.2 rounded-full text-[9px] font-mono font-extrabold flex items-center gap-0.5 shrink-0 ${
                              certAlertEvaluation.expiredCount > 0 
                                ? 'bg-red-500 text-white animate-pulse' 
                                : 'bg-amber-500 text-black animate-pulse'
                            }`}
                            title={`${certAlertEvaluation.urgentTotalCount} certificaciones por vencer (<30 días)`}
                          >
                            <span>⚠️</span>
                            <span>{certAlertEvaluation.urgentTotalCount}</span>
                          </span>
                        )}
                      </button>
                    )}

                    {/* 3. Operaciones */}
                    {isMenuAllowed('internal_operaciones') && (
                      <button
                        onClick={() => {
                          setActiveTab('internal_submenu');
                          setActiveInternalSubmenu('operaciones');
                          setExpandedClientSidebarId(null);
                        }}
                        className={`w-full text-left px-2 py-1.5 rounded-lg text-[10px] font-semibold transition-all flex items-center gap-2 cursor-pointer ${
                          activeTab === 'internal_submenu' && activeInternalSubmenu === 'operaciones'
                            ? 'bg-brand-red text-white font-bold pl-2 shadow-xs'
                            : 'text-[#c2c5c2] hover:text-white hover:bg-[#343634]'
                        }`}
                      >
                        <span className="text-xs">💼</span>
                        <span className="truncate">Operaciones</span>
                      </button>
                    )}

                    {/* 4. Recursos Humanos */}
                    {isMenuAllowed('internal_rrhh') && (
                      <button
                        onClick={() => {
                          setActiveTab('internal_submenu');
                          setActiveInternalSubmenu('rrhh');
                          setExpandedClientSidebarId(null);
                        }}
                        className={`w-full text-left px-2 py-1.5 rounded-lg text-[10px] font-semibold transition-all flex items-center gap-2 cursor-pointer ${
                          activeTab === 'internal_submenu' && activeInternalSubmenu === 'rrhh'
                            ? 'bg-brand-red text-white font-bold pl-2 shadow-xs'
                            : 'text-[#c2c5c2] hover:text-white hover:bg-[#343634]'
                        }`}
                      >
                        <span className="text-xs">❤️</span>
                        <span className="truncate">Recursos Humanos</span>
                      </button>
                    )}

                    {/* 5. Almacén */}
                    {isMenuAllowed('internal_almacen') && (
                      <button
                        onClick={() => {
                          setActiveTab('internal_submenu');
                          setActiveInternalSubmenu('almacen');
                          setExpandedClientSidebarId(null);
                        }}
                        className={`w-full text-left px-2 py-1.5 rounded-lg text-[10px] font-semibold transition-all flex items-center gap-2 cursor-pointer ${
                          activeTab === 'internal_submenu' && activeInternalSubmenu === 'almacen'
                            ? 'bg-brand-red text-white font-bold pl-2 shadow-xs'
                            : 'text-[#c2c5c2] hover:text-white hover:bg-[#343634]'
                        }`}
                      >
                        <span className="text-xs">🗑️</span>
                        <span className="truncate">Almacén</span>
                      </button>
                    )}

                    {/* 6. Activos */}
                    {isMenuAllowed('internal_activos') && (
                      <button
                        onClick={() => {
                          setActiveTab('internal_submenu');
                          setActiveInternalSubmenu('activos');
                          setExpandedClientSidebarId(null);
                        }}
                        className={`w-full text-left px-2 py-1.5 rounded-lg text-[10px] font-semibold transition-all flex items-center gap-2 cursor-pointer ${
                          activeTab === 'internal_submenu' && activeInternalSubmenu === 'activos'
                            ? 'bg-brand-red text-white font-bold pl-2 shadow-xs'
                            : 'text-[#c2c5c2] hover:text-white hover:bg-[#343634]'
                        }`}
                      >
                        <span className="text-xs">📦</span>
                        <span className="truncate">Activos</span>
                      </button>
                    )}

                    {/* 7. Mantenimiento */}
                    {isMenuAllowed('internal_mantenimiento') && (
                      <button
                        onClick={() => {
                          setActiveTab('internal_submenu');
                          setActiveInternalSubmenu('mantenimiento');
                          setExpandedClientSidebarId(null);
                        }}
                        className={`w-full text-left px-2 py-1.5 rounded-lg text-[10px] font-semibold transition-all flex items-center gap-2 cursor-pointer ${
                          activeTab === 'internal_submenu' && activeInternalSubmenu === 'mantenimiento'
                            ? 'bg-brand-red text-white font-bold pl-2 shadow-xs'
                            : 'text-[#c2c5c2] hover:text-white hover:bg-[#343634]'
                        }`}
                      >
                        <span className="text-xs">🔧</span>
                        <span className="truncate">Mantenimiento</span>
                      </button>
                    )}

                    {/* 8. IT-Sistemas */}
                    {isMenuAllowed('internal_it_sistemas') && (
                      <button
                        onClick={() => {
                          setActiveTab('internal_submenu');
                          setActiveInternalSubmenu('it_sistemas');
                          setExpandedClientSidebarId(null);
                        }}
                        className={`w-full text-left px-2 py-1.5 rounded-lg text-[10px] font-semibold transition-all flex items-center gap-2 cursor-pointer ${
                          activeTab === 'internal_submenu' && activeInternalSubmenu === 'it_sistemas'
                            ? 'bg-brand-red text-white font-bold pl-2 shadow-xs'
                            : 'text-[#c2c5c2] hover:text-white hover:bg-[#343634]'
                        }`}
                      >
                        <span className="text-xs">💻</span>
                        <span className="truncate">IT-Sistemas</span>
                      </button>
                    )}

                  </div>
                )}
              </div>
            )}

          {/* ADMIN PORTAL PANEL */}
          {isMenuAllowed('admin_portal') && (
            <div className="space-y-1 border-t border-[#52554e] pt-3">
              <button
                onClick={() => {
                  setActiveTab('admin_portal');
                  setExpandedClientSidebarId(null);
                  setActiveInternalSubmenu(null);
                }}
                className={`w-full text-left px-2.5 py-1.5 rounded-md text-[11px] font-bold admin-portal-btn flex items-center justify-between gap-2 cursor-pointer border transition-all ${
                  activeTab === 'admin_portal'
                    ? 'bg-brand-red text-white border-brand-red shadow-xs'
                    : 'text-[#bababa] bg-[#242624] hover:bg-[#3a3d3a] border-[#52554e]'
                }`}
                id="sidebar-tab-admin-portal"
              >
                <span className="flex items-center gap-2 truncate">
                  <Shield className="h-3.5 w-3.5 text-brand-red shrink-0" />
                  <span className="truncate">Panel de Administración</span>
                </span>
                <ChevronRight className="h-3 w-3 text-slate-400" />
              </button>
            </div>
          )}

        </div>

        {/* SIDEBAR FOOTER: USER INFO & BOTÓN CERRAR SESIÓN */}
        <div className="p-3 border-t border-[#3a3d3a] bg-[#202120] space-y-2.5 shrink-0">
          <div className="flex items-center justify-between gap-2 px-1">
            <div className="flex items-center gap-2 min-w-0">
              {currentUser?.avatarUrl ? (
                <img src={currentUser.avatarUrl} alt="Avatar" className="h-7 w-7 rounded-full object-cover border border-brand-red shrink-0" referrerPolicy="no-referrer" />
              ) : (
                <div className="h-7 w-7 rounded-full bg-brand-red text-white flex items-center justify-center font-bold text-xs shrink-0 shadow-xs">
                  {currentUser?.name ? currentUser.name.charAt(0).toUpperCase() : 'U'}
                </div>
              )}
              <div className="min-w-0">
                <p className="text-[11px] font-bold text-white truncate leading-tight">{currentUser?.name || 'Usuario'}</p>
                <span className="text-[8px] font-mono text-brand-red uppercase font-bold tracking-wider">
                  {currentUser?.role === 'admin' ? 'Administrador' : currentUser?.role === 'operator' ? 'Operador' : 'Socio Comercial'}
                </span>
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={handleLogout}
            id="sidebar-logout-btn"
            className="w-full bg-[#292b29] hover:bg-red-950/80 text-slate-200 hover:text-white border border-[#454845] hover:border-brand-red font-bold py-2 px-3 rounded-xl text-xs transition-all flex items-center justify-center gap-2 cursor-pointer shadow-sm group active:scale-[0.98]"
            title="Cerrar sesión y salir del sistema"
          >
            <LogOut className="h-4 w-4 text-brand-red group-hover:scale-110 transition-transform" />
            <span>Cerrar Sesión</span>
          </button>
        </div>

      </nav>

      {/* MAIN DISPLAY VIEWPORT */}
      <main className="flex-1 w-full max-w-full h-full max-h-[100dvh] overflow-y-auto overflow-x-hidden bg-brand-metal-soft p-3 sm:p-4 md:p-6 pb-28 sm:pb-8 md:pb-6 custom-scrollbar smooth-scroll overscroll-contain">
          
          {/* FCM Push Notification Realtime Alert Toast Banner */}
          {fcmAlertBanner && (
            <div className="mb-4 bg-gradient-to-r from-red-950 via-slate-900 to-red-950 border-2 border-brand-red text-white p-4 rounded-xl shadow-2xl flex items-center justify-between gap-4 animate-fadeIn">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-brand-red text-white rounded-xl shadow-md shrink-0">
                  <Bell className="h-5 w-5 animate-bounce" />
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[9px] font-mono font-bold bg-brand-red text-white px-1.5 py-0.5 rounded uppercase tracking-wider">
                      Push FCM Realtime
                    </span>
                    <h4 className="text-xs font-bold text-white font-display">{fcmAlertBanner.title}</h4>
                  </div>
                  <p className="text-xs text-slate-300 mt-0.5 leading-snug">{fcmAlertBanner.body}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {fcmAlertBanner.folioId && (
                  <button
                    onClick={() => {
                      setActiveTab('client_submenu');
                      setActiveClientSubmenu('trazabilidad');
                      setFcmAlertBanner(null);
                    }}
                    className="bg-brand-red hover:bg-red-700 text-white font-bold text-xs px-3 py-1.5 rounded-lg transition-all cursor-pointer shadow-sm"
                  >
                    Ver Folio
                  </button>
                )}
                <button
                  onClick={() => setFcmAlertBanner(null)}
                  className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-slate-800 transition-all cursor-pointer"
                  title="Cerrar alerta"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {/* TOP MINIMAL ACTION CONTROLS */}
          <div className="mb-4 flex items-center justify-end gap-3 relative z-[90]">
            {/* Right: Floating User Profile Bubble Trigger & Modal Popover (Overlays in front) */}
            <div className="relative shrink-0 z-[100]" ref={profileDropdownRef}>
              <button
                type="button"
                onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                className="flex items-center gap-2.5 p-1.5 pl-2 pr-3 bg-[#2e302e] hover:bg-[#3a3d3a] text-white rounded-full transition-all cursor-pointer shadow-lg border border-[#52554e] group"
              >
                {currentUser?.avatarUrl ? (
                  <img src={currentUser.avatarUrl} alt="Avatar" className="h-7 w-7 rounded-full object-cover border border-brand-red/80" referrerPolicy="no-referrer" />
                ) : (
                  <div className="h-7 w-7 rounded-full bg-brand-red text-white flex items-center justify-center font-bold text-xs shadow-xs">
                    {currentUser?.name ? currentUser.name.charAt(0).toUpperCase() : 'U'}
                  </div>
                )}
                <div className="text-left hidden sm:block leading-tight pr-0.5">
                  <p className="text-[11px] font-bold text-white truncate max-w-[120px]">{currentUser?.name || 'Usuario'}</p>
                  <span className="text-[8px] font-mono font-semibold text-brand-red block uppercase tracking-wider">
                    {currentUser?.role === 'admin' ? 'Administrador' : currentUser?.role === 'operator' ? 'Operador' : 'Socio Comercial'}
                  </span>
                </div>
                <ChevronDown className={`h-3.5 w-3.5 text-[#bababa] transition-transform group-hover:text-white ${isProfileDropdownOpen ? 'rotate-180 text-brand-red' : ''}`} />
              </button>

              {/* USER PROFILE POPOVER MODAL (OVERLAYS IN FRONT OF EVERYTHING WITH Z-[9999]) */}
              {isProfileDropdownOpen && (
                <div className="absolute right-0 top-full mt-2 w-80 bg-[#2e302e] border border-[#52554e] rounded-2xl shadow-2xl z-[9999] p-4 space-y-3.5 text-white animate-in fade-in slide-in-from-top-2">
                  {/* Header info */}
                  <div className="flex items-center gap-3 pb-3 border-b border-[#52554e]">
                    {currentUser?.avatarUrl ? (
                      <img src={currentUser.avatarUrl} alt="Avatar" className="h-10 w-10 rounded-full object-cover border border-brand-red shadow-sm" referrerPolicy="no-referrer" />
                    ) : (
                      <div className="h-10 w-10 rounded-full bg-brand-red text-white flex items-center justify-center font-bold text-sm shadow-sm">
                        {currentUser?.name ? currentUser.name.charAt(0).toUpperCase() : 'U'}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-bold text-white truncate">{currentUser?.name || 'Usuario'}</p>
                      <p className="text-[9.5px] text-[#bababa] font-mono truncate">{currentUser?.email || ''}</p>
                      <span className="text-[8px] font-mono font-bold text-white bg-brand-red/90 border border-brand-red px-1.5 py-0.5 rounded-md uppercase inline-block mt-1">
                        Rol: {currentUser?.role === 'admin' ? 'Administrador' : currentUser?.role === 'operator' ? 'Operador' : 'Socio Comercial'}
                      </span>
                    </div>
                  </div>

                  {/* SYSTEM CONTROLS INSIDE PROFILE BUBBLE: 24/7, COLA SINC, ALERTAS */}
                  <div className="bg-[#242624] border border-[#52554e] p-2.5 rounded-xl space-y-2">
                    <p className="text-[8px] font-bold text-[#bababa] uppercase tracking-wider font-mono">Estado del Sistema & Alertas</p>
                    
                    <div className="grid grid-cols-1 gap-1.5">
                      {/* 24/7 Status */}
                      <div className={`flex items-center justify-between px-2.5 py-1.5 rounded-lg text-[10px] font-mono font-bold border ${
                        isOnline ? 'bg-emerald-950/60 border-emerald-700/60 text-emerald-300' : 'bg-amber-950/60 border-amber-700/60 text-amber-300'
                      }`}>
                        <span className="flex items-center gap-1.5">
                          {isOnline ? <Wifi className="h-3.5 w-3.5 text-emerald-400" /> : <WifiOff className="h-3.5 w-3.5 text-amber-400" />}
                          24/7 En Línea
                        </span>
                        <span className="text-[9px] uppercase px-1.5 py-0.5 rounded bg-emerald-900/50 text-emerald-300">{isOnline ? 'Activo' : 'Offline'}</span>
                      </div>

                      {/* Cola Sinc Button */}
                      <button
                        type="button"
                        onClick={() => {
                          setIsSyncQueueOpen(true);
                          setIsProfileDropdownOpen(false);
                        }}
                        className="w-full flex items-center justify-between px-2.5 py-1.5 bg-[#2e302e] hover:bg-[#3a3d3a] border border-[#52554e] text-white rounded-lg text-[10px] font-bold transition-all cursor-pointer"
                      >
                        <span className="flex items-center gap-1.5">
                          <RefreshCw className={`h-3.5 w-3.5 ${pendingActions.length > 0 ? 'animate-spin text-amber-400' : 'text-[#bababa]'}`} />
                          Cola Sincronización
                        </span>
                        {pendingActions.length > 0 ? (
                          <span className="bg-amber-500 text-white rounded-full text-[8px] font-black h-4 px-1.5 flex items-center justify-center">
                            {pendingActions.length} pend
                          </span>
                        ) : (
                          <span className="text-[9px] text-[#bababa] font-mono">0 pendientes</span>
                        )}
                      </button>

                      {/* Alertas Button */}
                      <button
                        type="button"
                        onClick={requestNotificationPermission}
                        className={`w-full flex items-center justify-between px-2.5 py-1.5 border rounded-lg text-[10px] font-bold transition-all cursor-pointer ${
                          notificationPermission === 'granted'
                            ? 'bg-emerald-950/40 border-emerald-800/60 text-emerald-300'
                            : 'bg-[#2e302e] hover:bg-[#3a3d3a] border-[#52554e] text-white'
                        }`}
                      >
                        <span className="flex items-center gap-1.5">
                          <Bell className={`h-3.5 w-3.5 ${notificationPermission === 'granted' ? 'text-emerald-400' : 'text-[#bababa]'}`} />
                          Alertas Notificaciones
                        </span>
                        <span className="text-[8.5px] font-mono px-1.5 py-0.5 rounded bg-[#242624]">
                          {notificationPermission === 'granted' ? 'Activado' : 'Activar'}
                        </span>
                      </button>

                      {/* Configuración Avanzada de Alertas DC-3 (STPS) */}
                      <button
                        type="button"
                        onClick={() => {
                          setIsDC3ConfigModalOpen(true);
                          setIsProfileDropdownOpen(false);
                        }}
                        className="w-full flex items-center justify-between px-2.5 py-1.5 bg-gradient-to-r from-amber-950/50 via-[#2e302e] to-[#2e302e] hover:from-amber-900/60 hover:to-slate-800 border border-amber-500/40 hover:border-amber-400 text-white rounded-lg text-[10px] font-bold transition-all cursor-pointer group shadow-xs"
                      >
                        <span className="flex items-center gap-1.5 text-amber-200 group-hover:text-amber-100">
                          <Bell className="h-3.5 w-3.5 text-amber-400 group-hover:scale-110 transition-transform" />
                          <span>Alertas Formatos DC-3</span>
                        </span>
                        <span className="text-[8px] font-mono font-bold px-1.5 py-0.5 rounded bg-amber-500/25 text-amber-300 border border-amber-500/40 uppercase">
                          Avanzado
                        </span>
                      </button>
                    </div>
                  </div>

                  {/* Edit profile form */}
                  <div className="space-y-2 text-left">
                    <div className="space-y-0.5">
                      <label className="text-[8px] font-bold text-[#bababa] uppercase tracking-wider font-mono block">Nombre de Usuario</label>
                      <input
                        type="text"
                        value={tempName}
                        onChange={(e) => setTempName(e.target.value)}
                        className="bg-[#242624] border border-[#52554e] focus:border-brand-red text-[10px] text-white rounded-lg px-2.5 py-1.5 w-full focus:outline-hidden transition-all"
                      />
                    </div>

                    <div className="space-y-0.5">
                      <label className="text-[8px] font-bold text-[#bababa] uppercase tracking-wider font-mono block">Correo de Acceso</label>
                      <input
                        type="email"
                        value={tempEmail}
                        onChange={(e) => setTempEmail(e.target.value)}
                        className="bg-[#242624] border border-[#52554e] focus:border-brand-red text-[10px] text-white rounded-lg px-2.5 py-1.5 w-full focus:outline-hidden transition-all"
                      />
                    </div>

                    <div className="space-y-0.5">
                      <label className="text-[8px] font-bold text-[#bababa] uppercase tracking-wider font-mono block">Teléfono Móvil</label>
                      <input
                        type="text"
                        value={tempPhone}
                        onChange={(e) => setTempPhone(e.target.value)}
                        className="bg-[#242624] border border-[#52554e] focus:border-brand-red text-[10px] text-white rounded-lg px-2.5 py-1.5 w-full focus:outline-hidden transition-all"
                      />
                    </div>

                    <div className="space-y-0.5">
                      <label className="text-[8px] font-bold text-[#bababa] uppercase tracking-wider font-mono block">Foto (URL o Subir)</label>
                      <div className="flex items-center gap-1.5">
                        <input
                          type="text"
                          value={tempAvatarUrl}
                          onChange={(e) => setTempAvatarUrl(e.target.value)}
                          className="bg-[#242624] border border-[#52554e] focus:border-brand-red text-[10px] text-white rounded-lg px-2.5 py-1.5 w-full focus:outline-hidden transition-all truncate"
                        />
                        <label className="bg-[#242624] hover:bg-[#3a3d3a] border border-[#52554e] p-1.5 rounded-lg cursor-pointer shrink-0 flex items-center justify-center transition-all" title="Subir Foto">
                          <Camera className="h-3.5 w-3.5 text-[#bababa]" />
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleAvatarFileUpload}
                            className="hidden"
                          />
                        </label>
                      </div>
                    </div>

                    {/* Profile actions */}
                    <div className="flex items-center gap-2 pt-2 border-t border-[#52554e]">
                      <button
                        type="button"
                        onClick={handleCancelProfileEdit}
                        className="flex-1 bg-[#242624] hover:bg-[#3a3d3a] text-white font-bold py-1.5 px-2 rounded-lg text-[9px] cursor-pointer transition-colors"
                      >
                        Cancelar
                      </button>
                      <button
                        type="button"
                        onClick={handleSaveProfileEdit}
                        className="flex-1 bg-brand-red hover:bg-brand-red-hover text-white font-bold py-1.5 px-2 rounded-lg text-[9px] cursor-pointer transition-all flex items-center justify-center gap-1 shadow-sm"
                      >
                        <Save className="h-3 w-3" />
                        Guardar
                      </button>
                    </div>

                    {/* Direct Admin Portal link */}
                    {isMenuAllowed('admin_portal') && (
                      <button
                        type="button"
                        onClick={() => {
                          setActiveTab('admin_portal');
                          setExpandedClientSidebarId(null);
                          setActiveInternalSubmenu(null);
                          setIsProfileDropdownOpen(false);
                        }}
                        className="w-full bg-[#242624] hover:bg-brand-red border border-[#52554e] text-brand-red hover:text-white font-bold py-1.5 rounded-lg text-[9px] cursor-pointer transition-all flex items-center justify-center gap-1 mt-1"
                      >
                        <Shield className="h-3 w-3" />
                        Ir a Panel Control Admin
                      </button>
                    )}

                    {/* LOGOUT BUTTON */}
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="w-full bg-[#242624] hover:bg-red-900/60 hover:text-white text-[#bababa] border border-[#52554e] font-bold py-1.5 rounded-lg text-[9px] cursor-pointer transition-all flex items-center justify-center gap-1 mt-1"
                    >
                      <LogOut className="h-3 w-3" />
                      Cerrar Sesión
                    </button>

                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Persistent offline queue banner */}
          {pendingActions.length > 0 && (
            <div className="mb-5 bg-amber-500/10 border border-amber-500/20 text-amber-500 p-4 rounded-xl text-xs font-medium flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 shadow-md">
              <div className="flex items-center gap-2.5">
                <span className="relative flex h-2 w-2 shrink-0">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
                </span>
                <span className="font-sans leading-normal">
                  Tienes <strong className="text-white font-black">{pendingActions.length} cambio(s)</strong> en cola de sincronización automática. Operando en modo local seguro.
                </span>
              </div>
              <div className="flex items-center gap-2 self-end sm:self-auto">
                <button
                  type="button"
                  onClick={() => setIsSyncQueueOpen(true)}
                  className="bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/20 text-amber-300 font-bold px-3 py-1.5 rounded-lg transition-all text-[11px] cursor-pointer"
                >
                  Ver Cola Pendiente
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    await dataService.syncPendingActions();
                  }}
                  className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-black px-3.5 py-1.5 rounded-lg transition-all text-[11px] cursor-pointer flex items-center gap-1 shadow-md shadow-amber-500/10 active:scale-95"
                >
                  <RefreshCw className="h-3 w-3 animate-spin-slow" />
                  Sincronizar Ahora
                </button>
              </div>
            </div>
          )}
          
          {/* DINAMIC PAGE HEADERS FOR ALL PAGES - STICKY TOP */}
          {(() => {
            if (activeTab === 'welcome' || activeTab === 'kpis') return null;
            
            let title = '';
            let subtitle = '';

            if (activeTab === 'dashboard') {
              title = 'Tablero de Control y Analítica';
              subtitle = 'Resumen de indicadores clave de rendimiento, volúmenes de operación e histórico de facturación.';
            } else if (activeTab === 'folios') {
              title = 'Trazabilidad de Folios Logísticos';
              subtitle = 'Seguimiento en tiempo real del estado de cada lote, historial de procesos y control de retrabajos.';
            } else if (activeTab === 'calendar') {
              title = logisticsSubtab === 'calendar' ? 'Plan de Operación y Logística' : 'Rastreo Satelital en Tiempo Real';
              subtitle = logisticsSubtab === 'calendar' 
                ? 'Calendario operativo mensual con programación de recolecciones, entregas y eventos especiales.'
                : 'Monitoreo geográfico de unidades en tránsito y choferes compartiendo ubicación estilo WhatsApp Live.';
            } else if (activeTab === 'billing') {
              title = 'Prefacturas & Cobros';
              subtitle = 'Gestión integral de prefacturas generadas por servicios de decapado químico/térmico, aprobaciones y saldos.';
            } else if (activeTab === 'meetings') {
              title = 'Sala de Meet Virtual';
              subtitle = 'Coordina llamadas de seguimiento operativo con socios comerciales, soporte técnico y gerencia de planta.';
            } else if (activeTab === 'catalog') {
              title = 'Catálogo de Mercancías Global';
              subtitle = 'Especificaciones de piezas, unidades de medida y tarifas oficiales sincronizadas en pesos y dólares.';
            } else if (activeTab === 'admin_portal') {
              title = 'Panel de Administración Global';
              subtitle = 'Configuración de socios comerciales, centros de costos, asignación de roles de usuario y bitácoras de sistema.';
            } else if (activeTab === 'clients') {
              title = 'Socio Comercial / Configuración';
              subtitle = 'Gestión de configuraciones específicas, asignación de centros de costos y enlace de clientes.';
            } else if (activeTab === 'client_submenu') {
              const activeClient = clients.find(c => c.id === expandedClientSidebarId) || clients[0];
              const clientName = activeClient ? activeClient.name : 'Socio Comercial';
              
              switch (activeClientSubmenu) {
                case 'dashboard':
                  title = `Tablero: ${clientName}`;
                  subtitle = `Métricas consolidadas de rendimiento operativo y financiero de ${clientName}.`;
                  break;
                case 'catalog':
                  title = `Catálogo Personalizado: ${clientName}`;
                  subtitle = `Catálogo de mercancías autorizadas, unidades de medida y tarifas especiales para ${clientName}.`;
                  break;
                case 'cargas_envios':
                  title = `Cargas & Envíos: ${clientName}`;
                  subtitle = `Registro e ingreso de nuevos lotes y folios de mercancía para ${clientName}.`;
                  break;
                case 'trazabilidad':
                  title = `Trazabilidad de Folios: ${clientName}`;
                  subtitle = `Seguimiento detallado en tiempo real de los lotes y procesos de ${clientName}.`;
                  break;
                case 'facturas_cobros':
                  title = `Prefacturas & Cobros: ${clientName}`;
                  subtitle = `Detalle de cobros pendientes, prefacturas autorizadas e histórico de facturación de ${clientName}.`;
                  break;
                case 'historial':
                  title = `Historial General: ${clientName}`;
                  subtitle = `Archivo histórico completo de operaciones y folios finalizados de ${clientName}.`;
                  break;
                case 'reprocesos':
                  title = isClientUser(currentUser) ? `Retrabajos Cliente: ${clientName}` : `Reprocesos & Retrabajos: ${clientName}`;
                  subtitle = isClientUser(currentUser)
                    ? `Registro y seguimiento de retrabajos de cliente e incidencias de entrega de ${clientName}.`
                    : `Registro interno de lotes en retrabajo por calidad y retrabajos de cliente de ${clientName}.`;
                  break;
                case 'kpis':
                  title = `KPIS & Eficiencia Operativa: ${clientName}`;
                  subtitle = `Métricas de piezas entregadas vs retrabajos y porcentaje de eficiencia en relación a la meta institucional del 100%.`;
                  break;
                case 'alertas':
                  title = `Centro de Alertas: ${clientName}`;
                  subtitle = `Notificaciones en tiempo real sobre demoras de recolección, desviaciones de calidad y avisos urgentes.`;
                  break;
                default:
                  title = `${clientName}`;
                  subtitle = `Gestión de servicios de decapado industrial.`;
              }
            } else if (activeTab === 'internal_submenu') {
              const ccName = activeInternalCostCenter?.name || 'Grupo ISN';
              const ccCode = activeInternalCostCenter?.code ? ` [${activeInternalCostCenter.code}]` : '';
              switch (activeInternalSubmenu) {
                case 'proyectos':
                  title = `Proyectos Internos: ${ccName}${ccCode}`;
                  subtitle = `Hitos de expansión, paros técnicos y obras de infraestructura en ${ccName}.`;
                  break;
                case 'seguridad':
                  title = `Seguridad y HSEQ: ${ccName}${ccCode}`;
                  subtitle = `Cruz de seguridad, pláticas de 5 min, formatos DC-3, certificados y matriz de competencias en ${ccName}.`;
                  break;
                case 'operaciones':
                  title = `Control de Operaciones: ${ccName}${ccCode}`;
                  subtitle = `Monitoreo de procesos y folios de decapado en ${ccName}.`;
                  break;
                case 'rrhh':
                  title = `Recursos Humanos y Turnos: ${ccName}${ccCode}`;
                  subtitle = `Control de operadores, técnicos de decapado y bitácora de guardias en ${ccName}.`;
                  break;
                case 'almacen':
                  title = `Almacén y CEDIS: ${ccName}${ccCode}`;
                  subtitle = `Capacidad de almacenamiento de químicos, racks y piezas procesadas en ${ccName}.`;
                  break;
                case 'activos':
                  title = `Inventario de Activos y OEE: ${ccName}${ccCode}`;
                  subtitle = `Eficiencia de hornos térmicos, albercas químicas y grúas en ${ccName}.`;
                  break;
                case 'mantenimiento':
                  title = `Mantenimiento Programado: ${ccName}${ccCode}`;
                  subtitle = `Pruebas de pH, mantenimiento preventivo de hornos e inyectores en ${ccName}.`;
                  break;
                case 'it_sistemas':
                  title = `Consola IT y Telemetría: ${ccName}${ccCode}`;
                  subtitle = `Estado de enlaces, telemetría y diagnósticos de conectividad en ${ccName}.`;
                  break;
                default:
                  title = `Operación Interna: ${ccName}${ccCode}`;
                  subtitle = `Gestión técnica, operativa y de seguridad en ${ccName}.`;
              }
            }

            return (
              <div className="sticky -top-6 -mt-6 -mx-6 px-6 py-3.5 bg-white/90 backdrop-blur-md z-30 border-b border-slate-200/80 shadow-xs mb-6 flex items-center justify-between transition-all">
                <div>
                  <h2 className="text-base font-black text-slate-900 tracking-tight uppercase font-sans">
                    {title}
                  </h2>
                  <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed max-w-4xl font-medium">
                    {subtitle}
                  </p>
                </div>
                <div className="hidden sm:flex items-center gap-1.5 px-3 py-1 bg-slate-50/80 rounded-lg border border-slate-200/60 text-[10px] font-mono text-slate-500 shrink-0 shadow-2xs">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                  Sincronizado
                </div>
              </div>
            );
          })()}
          
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab + '-' + (activeTab === 'client_submenu' ? activeClientSubmenu + '-' + (expandedClientSidebarId || 'def') : activeInternalSubmenu || '')}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.15, ease: 'easeOut' }}
            >
              
              {/* RENDER ACTIVE VIEW */}
              {activeTab === 'welcome' && (
                <WelcomePage
                  currentUser={currentUser}
                  folios={folios}
                  preInvoices={preInvoices}
                  meetings={meetings}
                  exchangeRate={exchangeRate}
                  certAlertEvaluation={certAlertEvaluation}
                  onNavigate={(tab, sub) => {
                    setActiveTab(tab as TabType);
                    if (sub) {
                      if (tab === 'internal_submenu') {
                        setActiveInternalSubmenu(sub as any);
                      } else {
                        setActiveClientSubmenu(sub);
                      }
                    }
                  }}
                />
              )}

              {activeTab === 'kpis' && (
                <KpisManager
                  folios={folios}
                  clients={clients}
                  costCenters={costCenters}
                  currentUser={currentUser}
                  selectedCostCenter={selectedCostCenter?.id}
                  activeClient={isClientUser(currentUser) ? clients.find(c => c.code === currentUser?.clientCode || c.id === currentUser?.clientCode) || null : null}
                />
              )}

              {activeTab === 'dashboard' && !isClientUser(currentUser) && (
                <DashboardStats 
                  folios={selectedCostCenter ? folios.filter(f => f.costCenterId === selectedCostCenter.id || f.clientId === selectedCostCenter.clientId) : folios} 
                  preInvoices={preInvoices} 
                  exchangeRate={exchangeRate} 
                  certAlertEvaluation={certAlertEvaluation}
                  onNavigateToCertificates={() => {
                    setActiveTab('internal_submenu');
                    setActiveInternalSubmenu('seguridad');
                  }}
                />
              )}

              {activeTab === 'folios' && (() => {
                const isUserAClient = isClientUser(currentUser);
                const isChofer = isChoferUser(currentUser);
                const userClient = (isUserAClient || isChofer) ? (
                  clients.find(c => 
                    (currentUser?.clientCode && (c.code === currentUser.clientCode || c.id === currentUser.clientCode || c.name === currentUser.clientCode)) ||
                    (currentUser?.assignedClientIds && (currentUser.assignedClientIds.includes(c.id) || currentUser.assignedClientIds.includes(c.code))) ||
                    (currentUser?.assignedCostCenterIds && c.costCenterIds && c.costCenterIds.some(ccid => currentUser.assignedCostCenterIds?.includes(ccid))) ||
                    (currentUser?.name && c.name.toLowerCase().includes(currentUser.name.toLowerCase()))
                  ) || clients[0]
                ) : null;

                let activeCcList = userClient 
                  ? costCenters.filter(cc => 
                      cc.clientId === userClient.id || 
                      cc.clientId === userClient.code || 
                      cc.clientName === userClient.name ||
                      (cc.clientIds && (cc.clientIds.includes(userClient.id) || cc.clientIds.includes(userClient.code))) ||
                      (userClient.costCenterIds && userClient.costCenterIds.includes(cc.id))
                    )
                  : selectedCostCenter
                    ? [selectedCostCenter]
                    : costCenters;

                if (isChofer && currentUser?.assignedCostCenterIds && currentUser.assignedCostCenterIds.length > 0) {
                  const allowedCcIds = new Set(currentUser.assignedCostCenterIds);
                  activeCcList = activeCcList.filter(cc => allowedCcIds.has(cc.id) || allowedCcIds.has(cc.code) || allowedCcIds.has(cc.name));
                }

                const activeCcIds = new Set(activeCcList.map(cc => cc.id));

                let scopedFolios = userClient 
                  ? folios.filter(f => f.clientId === userClient.id || f.clientId === userClient.code || f.clientName === userClient.name)
                  : selectedCostCenter 
                    ? folios.filter(f => f.costCenterId === selectedCostCenter.id || f.clientId === selectedCostCenter.clientId)
                    : folios;

                if (isChofer && activeCcIds.size > 0) {
                  scopedFolios = scopedFolios.filter(f => f.costCenterId && activeCcIds.has(f.costCenterId));
                }

                const scopedClients = userClient
                  ? [userClient]
                  : selectedCostCenter
                    ? clients.filter(c => c.id === selectedCostCenter.clientId || c.code === selectedCostCenter.clientId || (selectedCostCenter.clientIds && selectedCostCenter.clientIds.includes(c.id)))
                    : clients;

                return (
                  <FolioManager
                    folios={scopedFolios}
                    clients={scopedClients}
                    costCenters={activeCcList}
                    destinations={destinations}
                    goods={goods}
                    activeClient={userClient || (selectedCostCenter ? scopedClients[0] : undefined)}
                    currentUser={currentUser}
                    onUpdateFolio={handleUpdateFolio}
                    onCreateFolio={handleCreateFolio}
                    onDeleteFolio={handleDeleteFolio}
                    selectedCostCenter={selectedCostCenter}
                    isOnline={isOnline}
                    defaultShowHistory={true}
                    isLoading={isLoading}
                  />
                );
              })()}

              {/* CLIENT NESTED SUBMENU VIEWPORT */}
              {activeTab === 'client_submenu' && (() => {
                const isUserAClient = isClientUser(currentUser);
                const isChofer = isChoferUser(currentUser);

                // Determine active client strictly:
                // 1. If an expanded client sidebar is active, that is the definitive active client
                // 2. If a cost center is selected, resolve the client owning that cost center
                // 3. If the logged in user is a client user or chofer with assigned clients/cost centers, resolve their client
                // 4. Fallback to the first client in the list
                let activeClient: Client | undefined = undefined;

                if (expandedClientSidebarId) {
                  activeClient = clients.find(c => c.id === expandedClientSidebarId || c.code === expandedClientSidebarId);
                }

                if (!activeClient && selectedCostCenter) {
                  activeClient = clients.find(c => 
                    c.id === selectedCostCenter.clientId || 
                    c.code === selectedCostCenter.clientId ||
                    (selectedCostCenter.clientIds && (selectedCostCenter.clientIds.includes(c.id) || selectedCostCenter.clientIds.includes(c.code))) ||
                    (c.costCenterIds && c.costCenterIds.includes(selectedCostCenter.id))
                  );
                }

                if (!activeClient && (isUserAClient || isChofer)) {
                  activeClient = clients.find(c => 
                    (currentUser?.clientCode && (c.code === currentUser.clientCode || c.id === currentUser.clientCode || c.name === currentUser.clientCode)) ||
                    (currentUser?.assignedClientIds && (currentUser.assignedClientIds.includes(c.id) || currentUser.assignedClientIds.includes(c.code))) ||
                    (currentUser?.assignedCostCenterIds && c.costCenterIds && c.costCenterIds.some(ccid => currentUser.assignedCostCenterIds?.includes(ccid))) ||
                    (currentUser?.name && c.name.toLowerCase().includes(currentUser.name.toLowerCase()))
                  );
                }

                if (!activeClient) {
                  activeClient = clients[0];
                }

                if (!activeClient) {
                  return (
                    <div className="p-8 text-center bg-white rounded-xl border border-slate-200">
                      <p className="text-xs text-slate-500 font-bold">Por favor seleccione un Socio Comercial en la barra lateral.</p>
                    </div>
                  );
                }

                // Scope cost centers strictly per client and user assignment
                let clientScopedCostCenters = costCenters.filter(cc => 
                  cc.clientId === activeClient.id || 
                  cc.clientId === activeClient.code || 
                  cc.clientName === activeClient.name ||
                  (cc.clientIds && (cc.clientIds.includes(activeClient.id) || cc.clientIds.includes(activeClient.code))) ||
                  (activeClient.costCenterIds && activeClient.costCenterIds.includes(cc.id))
                );

                if ((isUserAClient || isChofer) && currentUser?.assignedCostCenterIds && currentUser.assignedCostCenterIds.length > 0) {
                  const allowedCcIds = new Set(currentUser.assignedCostCenterIds);
                  clientScopedCostCenters = clientScopedCostCenters.filter(cc => allowedCcIds.has(cc.id) || allowedCcIds.has(cc.code) || allowedCcIds.has(cc.name));
                }

                const clientScopedCostCenterIds = new Set(clientScopedCostCenters.map(cc => cc.id));

                const clientAllCostCenterFolios = folios.filter(f => {
                  if (!f) return false;

                  const activeKeys = new Set([
                    activeClient.id?.toLowerCase(),
                    activeClient.code?.toLowerCase(),
                    activeClient.name?.toLowerCase()
                  ].filter(Boolean));

                  // 1. Direct match or explicit rejection by clientId
                  if (f.clientId) {
                    const fId = f.clientId.toLowerCase();
                    if (activeKeys.has(fId)) return true;

                    // If f.clientId points to another known client in system, reject immediately
                    const isOtherClient = clients.some(c => 
                      c.id !== activeClient.id && 
                      (c.id.toLowerCase() === fId || (c.code && c.code.toLowerCase() === fId) || c.name.toLowerCase() === fId)
                    );
                    if (isOtherClient) return false;
                  }

                  // 2. Direct match or explicit rejection by clientName
                  if (f.clientName) {
                    const fName = f.clientName.toLowerCase();
                    if (activeKeys.has(fName)) return true;

                    const isOtherClient = clients.some(c => 
                      c.id !== activeClient.id && 
                      (c.id.toLowerCase() === fName || (c.code && c.code.toLowerCase() === fName) || c.name.toLowerCase() === fName)
                    );
                    if (isOtherClient) return false;
                  }

                  // 3. Fallback match by cost center ID only
                  if (f.costCenterId && clientScopedCostCenterIds.has(f.costCenterId)) {
                    return true;
                  }

                  return false;
                });

                let clientScopedFolios = [...clientAllCostCenterFolios];

                // If a specific Cost Center is selected in the top dropdown, filter folios to that cost center
                if (selectedCostCenter) {
                  clientScopedFolios = clientScopedFolios.filter(f => 
                    f.costCenterId === selectedCostCenter.id || 
                    f.costCenterName === selectedCostCenter.name
                  );
                }

                // If user has assigned cost centers, isolate folios strictly to them
                if ((isUserAClient || isChofer) && currentUser?.assignedCostCenterIds && currentUser.assignedCostCenterIds.length > 0) {
                  const allowedCcIds = new Set(currentUser.assignedCostCenterIds);
                  clientScopedFolios = clientScopedFolios.filter(f => f.costCenterId && allowedCcIds.has(f.costCenterId));
                }

                const clientScopedGoods = goods.filter(g => 
                  g.clientId === activeClient.id || 
                  g.clientId === activeClient.code || 
                  g.clientName === activeClient.name
                );

                const sortedClients = [activeClient];

                const clientScopedDestinations = destinations.filter(d => 
                  !d.clientId || 
                  d.clientId === activeClient.id || 
                  d.clientId === activeClient.code || 
                  d.clientName === activeClient.name
                );

                return (
                  <div className="space-y-4">
                    {/* Submenu Routing */}
                    {activeClientSubmenu === 'dashboard' && (
                      <DashboardStats 
                        folios={clientScopedFolios} 
                        preInvoices={preInvoices.filter(inv => inv.clientId === activeClient.id || inv.clientName === activeClient.name)} 
                        exchangeRate={exchangeRate} 
                        certAlertEvaluation={certAlertEvaluation}
                        onNavigateToCertificates={() => {
                          setActiveTab('internal_submenu');
                          setActiveInternalSubmenu('seguridad');
                        }}
                      />
                    )}

                    {activeClientSubmenu === 'catalog' && (
                      <GoodsCatalog
                        goods={clientScopedGoods}
                        clientId={activeClient.id}
                        exchangeRate={exchangeRate}
                        lastUpdatedRate={lastUpdatedRate}
                        onRefreshExchangeRate={refreshExchangeRate}
                        onAddGoodsItem={handleAddGoodsItem}
                        onUpdateGoodsItem={handleUpdateGoodsItem}
                        onDeleteGoodsItem={handleDeleteGoodsItem}
                        currentUser={currentUser}
                        isLoading={isLoading}
                      />
                    )}

                    {activeClientSubmenu === 'cargas_envios' && (
                      <FolioManager
                        folios={clientScopedFolios}
                        clients={sortedClients}
                        costCenters={clientScopedCostCenters}
                        destinations={clientScopedDestinations}
                        goods={clientScopedGoods}
                        activeClient={activeClient}
                        selectedCostCenter={selectedCostCenter}
                        currentUser={currentUser}
                        onUpdateFolio={handleUpdateFolio}
                        onCreateFolio={handleCreateFolio}
                        onDeleteFolio={handleDeleteFolio}
                        isOnline={isOnline}
                        defaultShowHistory={true}
                        isLoading={isLoading}
                      />
                    )}

                    {activeClientSubmenu === 'trazabilidad' && (
                      <PublicTrackingPortal
                        initialClientId={activeClient.id}
                        folios={clientAllCostCenterFolios}
                        clients={sortedClients}
                        costCenters={clientScopedCostCenters}
                        destinations={clientScopedDestinations}
                        currentUser={currentUser}
                        onUpdateFolio={handleUpdateFolio}
                        isOnline={isOnline}
                        appLogoUrl={APP_LOGO_URL}
                        isClientViewOnly={isClientUser(currentUser)}
                      />
                    )}

                    {activeClientSubmenu === 'facturas_cobros' && (
                      <BillingManager
                        preInvoices={preInvoices.filter(inv => inv.clientId === activeClient.id || inv.clientName === activeClient.name)}
                        folios={clientScopedFolios}
                        clients={sortedClients}
                        activeClient={activeClient}
                        exchangeRate={exchangeRate}
                        onUpdateInvoice={handleUpdateInvoice}
                        onUpdateFolio={handleUpdateFolio}
                        currentUser={currentUser}
                        isClientViewOnly={isClientUser(currentUser)}
                      />
                    )}

                    {activeClientSubmenu === 'historial' && (
                      <PoHistoryPortal
                        folios={clientScopedFolios}
                        clients={sortedClients}
                        costCenters={clientScopedCostCenters}
                        currentUser={currentUser}
                        activeClient={activeClient}
                        isOnline={isOnline}
                      />
                    )}

                    {activeClientSubmenu === 'reprocesos' && (
                      <ReworkAndReturnsManager
                        folios={clientScopedFolios}
                        clients={sortedClients}
                        costCenters={clientScopedCostCenters}
                        goods={clientScopedGoods}
                        currentUser={currentUser}
                        activeClient={activeClient}
                        selectedCostCenter={selectedCostCenter}
                        onUpdateFolio={handleUpdateFolio}
                        onCreateFolio={handleCreateFolio}
                        isOnline={isOnline}
                      />
                    )}

                    {activeClientSubmenu === 'kpis' && (
                      <KpisManager
                        folios={clientScopedFolios}
                        clients={[activeClient]}
                        costCenters={clientScopedCostCenters}
                        currentUser={currentUser}
                        selectedCostCenter={selectedCostCenter?.id}
                        activeClient={activeClient}
                      />
                    )}

                    {activeClientSubmenu === 'alertas' && (
                      <AlertsAndNotifications 
                        client={activeClient} 
                        costCenter={selectedCostCenter} 
                        folios={clientScopedFolios} 
                        preInvoices={preInvoices.filter(inv => inv.clientId === activeClient.id || inv.clientName === activeClient.name)} 
                        currentUser={currentUser}
                        onNavigateToTab={(tab, clientSub) => {
                          setActiveTab('client_submenu');
                          setActiveClientSubmenu(clientSub);
                        }}
                      />
                    )}

                    {activeClientSubmenu === 'chat_soporte' && (
                      <ClientChatModule
                        client={activeClient}
                        costCenter={selectedCostCenter}
                        folios={clientScopedFolios}
                        preInvoices={preInvoices.filter(inv => inv.clientId === activeClient.id || inv.clientName === activeClient.name)}
                        currentUser={currentUser}
                      />
                    )}

                  </div>
                );
              })()}

              {/* INTERNAL SUBMENU VIEWPORT */}
              {activeTab === 'internal_submenu' && (
                <div className="space-y-6">
                  
                  {/* Top Cost Center Context & Fast Switcher Bar */}
                  <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white p-4 sm:p-5 rounded-2xl border border-slate-700/80 shadow-md flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-3.5">
                      <div className="w-11 h-11 rounded-xl bg-brand-red flex items-center justify-center text-xl shadow-inner shrink-0">
                        🏭
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[10px] font-mono font-bold bg-white/10 px-2 py-0.5 rounded text-slate-300 border border-white/10 uppercase">
                            Sede Seleccionada: {activeInternalCostCenter?.code || 'ISN-HQ'}
                          </span>
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                            Activo & Monitoreado
                          </span>
                        </div>
                        <h3 className="text-base sm:text-lg font-black text-white tracking-tight mt-0.5">
                          {activeInternalCostCenter?.name || 'Centro de Costos General'}
                        </h3>
                        <p className="text-xs text-slate-300">
                          {activeInternalCostCenter?.address || 'Instalaciones Grupo ISN'} • Resp: <span className="font-semibold text-white">{activeInternalCostCenter?.manager || 'Gerencia Operativa'}</span>
                        </p>
                      </div>
                    </div>

                    {/* Quick Switcher Chips */}
                    {activeInternalSubmenu !== 'seguridad' && activeInternalSubmenu !== 'proyectos' && (
                      <div className="flex flex-wrap items-center gap-1.5 pt-2 md:pt-0 border-t md:border-t-0 border-slate-700/60">
                        <span className="text-[10px] text-slate-400 font-mono font-bold mr-1">Cambiar Sede:</span>
                        {allowedCostCenters.map(cc => {
                          const isSelected = activeInternalCostCenter?.id === cc.id;
                          return (
                            <button
                              key={cc.id}
                              onClick={() => {
                                setActiveInternalCostCenterId(cc.id);
                                setSelectedCostCenter(cc);
                                setExpandedInternalCcId(cc.id);
                                setSelectedCostCenterFilter(cc.id);
                              }}
                              className={`text-[11px] font-bold px-2.5 py-1 rounded-lg transition-all cursor-pointer flex items-center gap-1 ${
                                isSelected
                                  ? 'bg-brand-red text-white shadow-sm ring-2 ring-white/30 scale-105'
                                  : 'bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white border border-slate-700'
                              }`}
                            >
                              <span>{cc.code?.replace('CC-', '') || cc.name.split(' ')[0]}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {activeInternalSubmenu === 'proyectos' && (
                    <ProjectsManager
                      key={`proj_mgr_${activeInternalCostCenter?.id || selectedCostCenter?.id || 'default_cc'}`}
                      activeCostCenter={activeInternalCostCenter || selectedCostCenter}
                      initialTab={activeProjectsSubtab}
                    />
                  )}

                  {activeInternalSubmenu === 'seguridad' && (
                    <SecurityInternalManager 
                      key={`sec_mgr_${activeInternalCostCenter?.id || selectedCostCenter?.id || 'default_cc'}`}
                      currentCostCenter={activeInternalCostCenter || selectedCostCenter}
                      costCenters={allowedCostCenters}
                      onSelectCostCenter={(cc) => {
                        setActiveInternalCostCenterId(cc.id);
                        setSelectedCostCenter(cc);
                        setExpandedInternalCcId(cc.id);
                        setSelectedCostCenterFilter(cc.id);
                      }}
                    />
                  )}

                  {activeInternalSubmenu === 'operaciones' && (
                    <FolioManager
                      folios={folios}
                      clients={clients}
                      costCenters={costCenters}
                      destinations={destinations}
                      goods={goods}
                      currentUser={currentUser}
                      onUpdateFolio={handleUpdateFolio}
                      onCreateFolio={handleCreateFolio}
                      onDeleteFolio={handleDeleteFolio}
                      isOnline={isOnline}
                      isLoading={isLoading}
                    />
                  )}

                  {activeInternalSubmenu === 'rrhh' && (
                    <div className="space-y-6">
                      <div className="bg-slate-900 text-white p-5 rounded-xl border border-slate-800 flex items-center justify-between">
                        <div>
                          <h3 className="text-lg font-display font-bold">Personal Operativo y Control de Turnos: {activeInternalCostCenter?.name}</h3>
                          <p className="text-xs text-slate-400 mt-0.5">Control de credenciales, certificaciones técnicas HSEQ y asignaciones de operadores en {activeInternalCostCenter?.address || 'la planta'}.</p>
                        </div>
                        <span className="text-xs font-mono bg-slate-800 text-slate-300 px-2.5 py-1 rounded-lg border border-slate-700">
                          {activeInternalCostCenter?.code || 'CC-ACTIVE'}
                        </span>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="bg-white p-5 rounded-xl border border-slate-200 space-y-4">
                          <h4 className="text-xs font-bold text-slate-900 border-b border-slate-100 pb-2">📋 Plantilla Asignada a {activeInternalCostCenter?.name}</h4>
                          <div className="space-y-3">
                            {[
                              { name: activeInternalCostCenter?.manager || 'Ing. Supervisor de Planta', role: 'Gerente / Jefe de Sede', cert: 'DC3 Integral HSEQ', status: 'Activo' },
                              { name: 'Téc. Operador Primario', role: 'Operador Hornos y Tinas', cert: 'Manejo de Químicos y Pyrolisis', status: 'Activo' },
                              { name: 'Chofer / Maniobrista', role: 'Logística de Decapado', cert: 'Montacargas y Licencia Federal', status: 'En Turno' }
                            ].map((op, i) => (
                              <div key={i} className="p-3 bg-slate-50 rounded-lg border border-slate-100 flex items-center justify-between text-xs">
                                <div>
                                  <p className="font-bold text-slate-900">{op.name}</p>
                                  <p className="text-[10px] text-slate-500 mt-0.5">{op.role} • Certificado: <span className="font-semibold text-brand-red">{op.cert}</span></p>
                                </div>
                                <span className={`text-[9px] font-bold px-2 py-0.5 rounded ${op.status === 'Activo' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>{op.status}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="bg-white p-5 rounded-xl border border-slate-200 flex flex-col justify-between">
                          <div className="space-y-2">
                            <h4 className="text-xs font-bold text-slate-900 border-b border-slate-100 pb-2">⏱️ Bitácora de Turnos de Hoy</h4>
                            <p className="text-xs text-slate-500 leading-relaxed">Se reporta plantilla al 100% de asistencia en {activeInternalCostCenter?.name}. Cumplimiento con las normas de descanso y relevo de guardia sin incidentes registrados.</p>
                          </div>
                          <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-100 text-emerald-800 text-[11px] font-semibold mt-4">
                            ⭐ Turno de Guardia en {activeInternalCostCenter?.name} validado con firma digital del supervisor {activeInternalCostCenter?.manager || ''}.
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeInternalSubmenu === 'almacen' && (
                    <div className="space-y-6">
                      <div className="bg-white p-5 rounded-xl border border-slate-200 flex items-center justify-between">
                        <div>
                          <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider font-mono">Almacén y CEDIS de {activeInternalCostCenter?.name}</h3>
                          <h4 className="text-xl font-display font-bold text-slate-900 mt-1">Ocupación y Almacenamiento de Sustancias Químicas y Racks</h4>
                        </div>
                        <span className="px-3 py-1 bg-slate-100 text-slate-700 rounded-lg font-mono text-xs font-bold border border-slate-200">
                          {activeInternalCostCenter?.code}
                        </span>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="bg-white p-5 rounded-xl border-2 border-brand-red/60 text-center shadow-xs">
                          <span className="text-[10px] font-bold text-brand-red uppercase tracking-wider block font-mono">Sede Actual: {activeInternalCostCenter?.name}</span>
                          <span className="text-3xl font-display font-bold text-slate-900 block mt-2">68%</span>
                          <span className="text-[10px] bg-green-50 text-green-700 font-bold px-2 py-0.5 rounded-full inline-block mt-2 border border-green-100">Capacidad Operativa Óptima</span>
                          <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden mt-4">
                            <div className="bg-brand-red h-full" style={{ width: '68%' }}></div>
                          </div>
                        </div>

                        {allowedCostCenters.filter(c => c.id !== activeInternalCostCenter?.id).slice(0, 2).map((otherCc, idx) => (
                          <div key={otherCc.id} className="bg-white p-5 rounded-xl border border-slate-200 text-center">
                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block font-mono">{otherCc.name}</span>
                            <span className="text-3xl font-display font-bold text-slate-800 block mt-2">{idx === 0 ? '45%' : '82%'}</span>
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full inline-block mt-2 border ${idx === 0 ? 'bg-green-50 text-green-700 border-green-100' : 'bg-amber-50 text-amber-700 border-amber-100'}`}>
                              {idx === 0 ? 'Capacidad Disponible' : 'Alta Demanda'}
                            </span>
                            <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden mt-4">
                              <div className={idx === 0 ? 'bg-emerald-500 h-full' : 'bg-amber-500 h-full'} style={{ width: idx === 0 ? '45%' : '82%' }}></div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {activeInternalSubmenu === 'activos' && (
                    <div className="space-y-6">
                      <div className="bg-slate-900 text-white p-5 rounded-xl border border-slate-800 flex items-center justify-between">
                        <div>
                          <h3 className="text-lg font-display font-bold">Catálogo de Activos: {activeInternalCostCenter?.name}</h3>
                          <p className="text-xs text-slate-400">Control físico e inventario de hornos, albercas de decapado y maquinaria pesada asignada.</p>
                        </div>
                        <span className="text-xs font-mono text-slate-400">Ubicación: {activeInternalCostCenter?.address}</span>
                      </div>

                      <div className="bg-white p-5 rounded-xl border border-slate-200">
                        <table className="w-full text-left text-xs text-slate-600 border-collapse">
                          <thead>
                            <tr className="border-b border-slate-100 text-[10px] font-bold uppercase text-slate-400 font-mono">
                              <th className="pb-3">Activo</th>
                              <th className="pb-3">Código</th>
                              <th className="pb-3">Sede</th>
                              <th className="pb-3">Estado</th>
                              <th className="pb-3">Eficiencia OEE</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {[
                              { name: `Horno Pirolítico Primario - ${activeInternalCostCenter?.code || '01'}`, code: `ACT-HPN-${activeInternalCostCenter?.code?.replace('CC-','') || '01'}`, loc: activeInternalCostCenter?.name || 'Sede', status: 'Activo', eff: '98.4%' },
                              { name: `Tina de Inmersión Química Ácida`, code: `ACT-TNA-${activeInternalCostCenter?.code?.replace('CC-','') || '02'}`, loc: activeInternalCostCenter?.name || 'Sede', status: 'Operando', eff: '96.1%' },
                              { name: `Sistema de Lavado Alta Presión Hidrojet`, code: `ACT-HDJ-${activeInternalCostCenter?.code?.replace('CC-','') || '03'}`, loc: activeInternalCostCenter?.name || 'Sede', status: 'Activo', eff: '99.0%' },
                              { name: `Montacargas Eléctrico 3.5 Toneladas`, code: `ACT-MNT-${activeInternalCostCenter?.code?.replace('CC-','') || '04'}`, loc: activeInternalCostCenter?.name || 'Sede', status: 'Activo', eff: '94.5%' }
                            ].map((act, i) => (
                              <tr key={i}>
                                <td className="py-3.5 font-bold text-slate-900">{act.name}</td>
                                <td className="py-3.5 font-mono">{act.code}</td>
                                <td className="py-3.5 font-semibold text-slate-700">{act.loc}</td>
                                <td className="py-3.5">
                                  <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-green-50 text-green-700 border border-green-100">{act.status}</span>
                                </td>
                                <td className="py-3.5 font-bold text-slate-800">{act.eff}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {activeInternalSubmenu === 'mantenimiento' && (
                    <div className="space-y-6">
                      <div className="bg-[#1e293b] text-white p-5 rounded-xl border border-slate-700 flex items-center justify-between">
                        <div>
                          <h3 className="text-lg font-display font-bold">Mantenimiento Programado: {activeInternalCostCenter?.name}</h3>
                          <p className="text-xs text-slate-300 mt-0.5">Control de pruebas de sensores de pH, recargas químicas y mantenimiento preventivo de hornos.</p>
                        </div>
                        <span className="text-xs font-mono bg-slate-800 text-slate-300 px-2 py-1 rounded border border-slate-700">
                          {activeInternalCostCenter?.code}
                        </span>
                      </div>

                      <div className="bg-white p-5 rounded-xl border border-slate-200">
                        <h4 className="text-xs font-bold text-slate-900 border-b border-slate-100 pb-3 mb-4">🔧 Checklist y Calibraciones Semanales en {activeInternalCostCenter?.name}</h4>
                        <div className="space-y-3.5">
                          {[
                            { title: `Calibración de electrodo pH en Tinas Ácidas (${activeInternalCostCenter?.code})`, desc: 'Ajuste de electrodo con soluciones buffer pH 4.0 y 7.0.', date: 'Programado: 22 Ago 2026', done: false },
                            { title: `Purga de Lodos y Sedimentos Decapados`, desc: `Retiro de sales y residuos en ${activeInternalCostCenter?.name}.`, date: 'Completado ayer', done: true },
                            { title: `Prueba de Quemadores de Gas Horno Pirolítico`, desc: 'Inspección de termopares, termostatos y combustión limpia.', date: 'Completado por el equipo técnico', done: true }
                          ].map((maint, i) => (
                            <div key={i} className="p-3 bg-slate-50 hover:bg-slate-100/50 rounded-xl border border-slate-200 flex items-start gap-3 transition-all">
                              <input 
                                type="checkbox" 
                                checked={maint.done} 
                                readOnly 
                                className="mt-1 shrink-0 accent-brand-red h-4 w-4" 
                              />
                              <div>
                                <h5 className={`text-xs font-bold text-slate-900 ${maint.done ? 'line-through text-slate-400' : ''}`}>{maint.title}</h5>
                                <p className="text-[10px] text-slate-500 mt-0.5">{maint.desc}</p>
                                <span className={`text-[9px] font-bold block mt-2 ${maint.done ? 'text-emerald-600' : 'text-slate-400'}`}>{maint.date}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {activeInternalSubmenu === 'it_sistemas' && (
                    <div className="space-y-6">
                      <div className="bg-slate-900 text-slate-100 p-5 rounded-xl border border-slate-800 flex items-center justify-between">
                        <div>
                          <h3 className="text-xs font-bold text-brand-red font-mono uppercase tracking-widest">Consola IT • Nodo {activeInternalCostCenter?.code}</h3>
                          <h4 className="text-lg font-display font-bold text-white mt-1">Diagnósticos y Telemetría en {activeInternalCostCenter?.name}</h4>
                        </div>
                        <span className="text-xs font-mono text-emerald-400 bg-emerald-950/60 px-2.5 py-1 rounded border border-emerald-800/60">
                          ● NODO EN LÍNEA
                        </span>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="bg-white p-5 rounded-xl border border-slate-200 space-y-4">
                          <h4 className="text-xs font-bold text-slate-900 border-b border-slate-100 pb-2">🛰️ Estado de Enlaces y Conectividad</h4>
                          <div className="space-y-3 font-mono text-[11px]">
                            <div className="flex justify-between items-center bg-slate-50 p-2.5 rounded border border-slate-100">
                              <span className="text-slate-500">Enlace Local {activeInternalCostCenter?.code}</span>
                              <span className="text-emerald-600 font-bold">● 100 Mbps (Fibra)</span>
                            </div>
                            <div className="flex justify-between items-center bg-slate-50 p-2.5 rounded border border-slate-100">
                              <span className="text-slate-500">Servicio Báscula y Terminal RFID</span>
                              <span className="text-emerald-600 font-bold">● Sincronizado</span>
                            </div>
                            <div className="flex justify-between items-center bg-slate-50 p-2.5 rounded border border-slate-100">
                              <span className="text-slate-500">Firestore Cloud Sync</span>
                              <span className="text-emerald-600 font-bold">● Latencia 18ms</span>
                            </div>
                          </div>
                        </div>

                        <div className="bg-slate-950 text-emerald-400 p-5 rounded-xl border border-slate-800 font-mono text-[10px] space-y-2 h-[180px] overflow-y-auto">
                          <p className="text-slate-500">[NODO INICIADO] - {activeInternalCostCenter?.name} ({activeInternalCostCenter?.code})</p>
                          <p className="text-slate-500">[DISPOSITIVOS] Sensores de planta y cámaras conectadas</p>
                          <p className="text-slate-400">[HSEQ-SYNC] Cruz de seguridad y DC3 sincronizados en tiempo real</p>
                          <p className="text-slate-400">[TELEMETRÍA] {activeInternalCostCenter?.address} conectado a la red central</p>
                          <p className="text-emerald-500">[ESTADO] Todos los servicios operativos</p>
                        </div>
                      </div>
                    </div>
                  )}

                </div>
              )}

              {activeTab === 'admin_portal' && (isAdminRole(currentUser.role) || currentUser.role === 'admin') && (
                <AdminPortal
                  clients={clients}
                  costCenters={costCenters}
                  destinations={destinations}
                  onAddClient={handleAddClient}
                  onAddCostCenter={handleAddCostCenter}
                  onUpdateClient={handleUpdateClient}
                  onDeleteClient={handleDeleteClient}
                  onUpdateCostCenter={handleUpdateCostCenter}
                  onDeleteCostCenter={handleDeleteCostCenter}
                  onAddDestination={handleAddDestination}
                  onUpdateDestination={handleUpdateDestination}
                  onDeleteDestination={handleDeleteDestination}
                  currentUser={currentUser}
                  onLogActivity={handleLogActivity}
                  onResetDatabase={handleResetDatabase}
                />
              )}

              {activeTab === 'clients' && !isClientRole(currentUser.role) && (
                <ClientConfig
                  clients={clients}
                  costCenters={costCenters}
                  onAddClient={handleAddClient}
                  onAddCostCenter={handleAddCostCenter}
                  currentUser={currentUser}
                />
              )}

              {activeTab === 'catalog' && (
                <GoodsCatalog
                  goods={goods}
                  exchangeRate={exchangeRate}
                  lastUpdatedRate={lastUpdatedRate}
                  onRefreshExchangeRate={refreshExchangeRate}
                  onAddGoodsItem={handleAddGoodsItem}
                  onUpdateGoodsItem={handleUpdateGoodsItem}
                  onDeleteGoodsItem={handleDeleteGoodsItem}
                  onDeleteMultipleGoodsItems={handleDeleteMultipleGoodsItems}
                  onDeleteAllGoodsItems={handleDeleteAllGoodsItems}
                  currentUser={currentUser}
                  isLoading={isLoading}
                />
              )}

              {activeTab === 'meetings' && (() => {
                const isUserAClient = isClientUser(currentUser);
                const userClient = isUserAClient ? (
                  clients.find(c => 
                    (currentUser?.clientCode && (c.code === currentUser.clientCode || c.id === currentUser.clientCode || c.name === currentUser.clientCode)) ||
                    (currentUser?.assignedClientIds && (currentUser.assignedClientIds.includes(c.id) || currentUser.assignedClientIds.includes(c.code))) ||
                    (currentUser?.name && c.name.toLowerCase().includes(currentUser.name.toLowerCase()))
                  ) || clients[0]
                ) : null;

                const scopedMeetings = isUserAClient && userClient
                  ? meetings.filter(m => m.clientId === userClient.id || m.clientName === userClient.name)
                  : meetings;

                return (
                  <MeetScheduler
                    meetings={scopedMeetings}
                    clients={userClient ? [userClient] : clients}
                    onAddMeeting={handleAddMeeting}
                    onUpdateMeeting={handleUpdateMeeting}
                    onDeleteMeeting={handleDeleteMeeting}
                    currentUser={currentUser}
                  />
                );
              })()}

              {activeTab === 'calendar' && (() => {
                const isUserAClient = isClientUser(currentUser);
                const userClient = isUserAClient ? (
                  clients.find(c => 
                    (currentUser?.clientCode && (c.code === currentUser.clientCode || c.id === currentUser.clientCode || c.name === currentUser.clientCode)) ||
                    (currentUser?.assignedClientIds && (currentUser.assignedClientIds.includes(c.id) || currentUser.assignedClientIds.includes(c.code))) ||
                    (currentUser?.name && c.name.toLowerCase().includes(currentUser.name.toLowerCase()))
                  ) || clients[0]
                ) : null;

                const scopedFolios = isUserAClient && userClient
                  ? folios.filter(f => f.clientId === userClient.id || f.clientName === userClient.name)
                  : folios;

                const scopedCostCenters = isUserAClient && userClient
                  ? costCenters.filter(cc => 
                      cc.clientId === userClient.id || 
                      cc.clientId === userClient.code || 
                      cc.clientName === userClient.name ||
                      (cc.clientIds && (cc.clientIds.includes(userClient.id) || cc.clientIds.includes(userClient.code))) ||
                      (userClient.costCenterIds && userClient.costCenterIds.includes(cc.id))
                    )
                  : costCenters;

                return (
                  <div className="space-y-4 font-sans">
                    {/* Premium Subtab selector for Logistics */}
                    <div className="flex border-b border-slate-200 gap-2 mb-2">
                      <button
                        onClick={() => setLogisticsSubtab('calendar')}
                        className={`py-2 px-4 text-[11px] font-bold uppercase border-b-2 transition-all flex items-center gap-1.5 cursor-pointer ${
                          logisticsSubtab === 'calendar'
                            ? 'border-brand-red text-slate-900 font-black'
                            : 'border-transparent text-slate-400 hover:text-slate-600 font-semibold'
                        }`}
                      >
                        📅 Calendario Operativo
                      </button>
                      <button
                        onClick={() => setLogisticsSubtab('map')}
                        className={`py-2 px-4 text-[11px] font-bold uppercase border-b-2 transition-all flex items-center gap-1.5 cursor-pointer ${
                          logisticsSubtab === 'map'
                            ? 'border-brand-red text-slate-900 font-black'
                            : 'border-transparent text-slate-400 hover:text-slate-600 font-semibold'
                        }`}
                      >
                        🚚 Mapa en Tiempo Real
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                      </button>
                    </div>

                    {logisticsSubtab === 'calendar' ? (
                      <LogisticsCalendar
                        events={calendarEvents}
                        folios={scopedFolios}
                        onAddEvent={handleAddCalendarEvent}
                        onUpdateFolio={handleUpdateFolio}
                      />
                    ) : (
                      <LogisticsMap
                        folios={scopedFolios}
                        costCenters={scopedCostCenters}
                        currentUser={currentUser as any}
                      />
                    )}
                  </div>
                );
              })()}

              {activeTab === 'billing' && (() => {
                const isUserAClient = isClientUser(currentUser);
                const userClient = isUserAClient ? (
                  clients.find(c => 
                    (currentUser?.clientCode && (c.code === currentUser.clientCode || c.id === currentUser.clientCode || c.name === currentUser.clientCode)) ||
                    (currentUser?.assignedClientIds && (currentUser.assignedClientIds.includes(c.id) || currentUser.assignedClientIds.includes(c.code))) ||
                    (currentUser?.name && c.name.toLowerCase().includes(currentUser.name.toLowerCase()))
                  ) || clients[0]
                ) : null;

                const scopedInvoices = isUserAClient && userClient
                  ? preInvoices.filter(inv => inv.clientId === userClient.id || inv.clientName === userClient.name)
                  : preInvoices;

                const scopedFolios = isUserAClient && userClient
                  ? folios.filter(f => f.clientId === userClient.id || f.clientName === userClient.name)
                  : folios;

                return (
                  <BillingManager
                    preInvoices={scopedInvoices}
                    folios={scopedFolios}
                    clients={userClient ? [userClient] : clients}
                    activeClient={userClient || undefined}
                    exchangeRate={exchangeRate}
                    onUpdateInvoice={handleUpdateInvoice}
                    onUpdateFolio={handleUpdateFolio}
                    currentUser={currentUser}
                    isClientViewOnly={isClientUser(currentUser)}
                  />
                );
              })()}

            </motion.div>
          </AnimatePresence>

        </main>

      {/* GLOBAL APP CUSTOM ALERT MODAL */}
      <AnimatePresence>
        {modalAlert.show && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" id="custom-app-alert-overlay">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => modalAlert.resolve?.()}
              className="absolute inset-0 bg-slate-950/40 backdrop-blur-xs"
            />
            {/* Modal Box */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
              transition={{ duration: 0.15, ease: 'easeOut' }}
              className="bg-white rounded-2xl shadow-xl border border-slate-200/80 w-full max-w-md overflow-hidden relative z-50 font-sans"
            >
              {/* Header */}
              <div className="p-4 border-b border-slate-100 flex items-center gap-3 bg-slate-50">
                <div className="p-2 bg-slate-100 text-brand-red rounded-lg">
                  <Bell className="h-4 w-4" />
                </div>
                <h3 className="font-black text-xs text-slate-800 uppercase tracking-tight">
                  {modalAlert.title}
                </h3>
              </div>
              
              {/* Body */}
              <div className="p-5">
                <p className="text-xs font-semibold text-slate-600 leading-relaxed whitespace-pre-wrap">
                  {modalAlert.message}
                </p>
              </div>

              {/* Footer */}
              <div className="p-3 bg-slate-50 border-t border-slate-100 flex justify-end">
                <button
                  id="btn-alert-ok"
                  onClick={() => modalAlert.resolve?.()}
                  className="px-4 py-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-[10px] font-black uppercase tracking-wider transition-all shadow-xs cursor-pointer"
                >
                  Entendido
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* GLOBAL APP CUSTOM CONFIRM MODAL */}
      <AnimatePresence>
        {modalConfirm.show && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" id="custom-app-confirm-overlay">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-slate-950/40 backdrop-blur-xs"
            />
            {/* Modal Box */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
              transition={{ duration: 0.15, ease: 'easeOut' }}
              className="bg-white rounded-2xl shadow-xl border border-slate-200/80 w-full max-w-md overflow-hidden relative z-50 font-sans"
            >
              {/* Header */}
              <div className="p-4 border-b border-slate-100 flex items-center gap-3 bg-slate-50">
                <div className="p-2 bg-amber-50 text-amber-600 rounded-lg">
                  <ShieldAlert className="h-4 w-4" />
                </div>
                <h3 className="font-black text-xs text-slate-800 uppercase tracking-tight">
                  {modalConfirm.title}
                </h3>
              </div>
              
              {/* Body */}
              <div className="p-5">
                <p className="text-xs font-semibold text-slate-600 leading-relaxed whitespace-pre-wrap">
                  {modalConfirm.message}
                </p>
              </div>

              {/* Footer */}
              <div className="p-3 bg-slate-50 border-t border-slate-100 flex justify-end gap-2">
                <button
                  id="btn-confirm-cancel"
                  onClick={() => modalConfirm.resolve?.(false)}
                  className="px-3 py-1.5 border border-slate-200 hover:bg-slate-100 text-slate-600 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  id="btn-confirm-yes"
                  onClick={() => modalConfirm.resolve?.(true)}
                  className="px-4 py-1.5 bg-brand-red hover:bg-brand-red-hover text-white rounded-lg text-[10px] font-black uppercase tracking-wider transition-all shadow-xs cursor-pointer"
                >
                  Confirmar
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* SYNC QUEUE DRAWER */}
      <AnimatePresence>
        {isSyncQueueOpen && (
          <div className="fixed inset-0 z-50 flex justify-end" id="sync-queue-drawer-overlay">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsSyncQueueOpen(false)}
              className="absolute inset-0 bg-slate-950/50 backdrop-blur-xs"
            />
            {/* Drawer Panel */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="relative w-full max-w-md bg-slate-900 border-l border-slate-800 h-full flex flex-col shadow-2xl text-slate-100 font-sans z-50"
            >
              {/* Header */}
              <div className="p-4 border-b border-slate-800 flex items-center justify-between bg-slate-950">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 bg-amber-500/10 text-amber-400 rounded-lg">
                    <RefreshCw className="h-4 w-4 animate-spin-slow" />
                  </div>
                  <div>
                    <h3 className="font-bold text-sm tracking-tight text-white">Cola de Acciones Pendientes</h3>
                    <p className="text-[10px] text-slate-400 font-medium">Sincronización fuera de línea y dual-persistence</p>
                  </div>
                </div>
                <button
                  onClick={() => setIsSyncQueueOpen(false)}
                  className="p-1.5 hover:bg-slate-800 text-slate-400 hover:text-white rounded-lg transition-colors cursor-pointer"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Body / Queue List */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3.5">
                <div className="bg-slate-800 p-3.5 rounded-xl border border-slate-700/60 text-[11px] text-slate-300 space-y-2">
                  <div className="flex items-center gap-1.5 font-bold text-amber-400">
                    <ShieldAlert className="h-4 w-4 shrink-0" />
                    <span>MODO SEGURO ACTIVO (OFFLINE CACHE)</span>
                  </div>
                  <p className="leading-relaxed text-slate-400">
                    Cada cambio que realizas sin internet se guarda de forma segura en la base de datos local (localStorage). Tan pronto recuperes la señal, el sistema enviará los cambios de forma secuencial.
                  </p>
                </div>

                {pendingActions.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center space-y-3 text-slate-500">
                    <div className="h-10 w-10 rounded-full bg-slate-800 flex items-center justify-center text-slate-400">
                      <CheckCircle className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-slate-300">¡Todo sincronizado!</p>
                      <p className="text-[10px] text-slate-500 max-w-xs mt-1">No hay operaciones pendientes de subir a la nube.</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2.5">
                    <div className="flex items-center justify-between pb-1">
                      <span className="text-[10px] font-bold font-mono tracking-wider text-slate-400 uppercase">Cambios En Cola ({pendingActions.length})</span>
                      <button 
                        onClick={() => {
                          if (window.confirm('¿Deseas descartar todas las acciones pendientes de sincronización?')) {
                            localStorage.setItem('snfsic_pending_actions', JSON.stringify([]));
                            setPendingActions([]);
                          }
                        }}
                        className="text-[10px] text-red-400 hover:underline hover:text-red-300 font-bold"
                      >
                        Descartar Todo
                      </button>
                    </div>
                    
                    {pendingActions.map((item) => (
                      <div 
                        key={item.id} 
                        className="bg-slate-950/80 p-3 rounded-lg border border-slate-800 flex items-start justify-between gap-3 group hover:border-slate-750 transition-colors"
                      >
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className={`px-1.5 py-0.5 rounded-xs text-[8px] font-black tracking-wider uppercase font-mono ${
                              item.action === 'save' 
                                ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' 
                                : 'bg-red-500/10 text-red-400 border border-red-500/20'
                            }`}>
                              {item.action === 'save' ? 'Guardar' : 'Eliminar'}
                            </span>
                            <span className="text-[8px] text-slate-500 font-mono">
                              {new Date(item.timestamp).toLocaleTimeString()}
                            </span>
                          </div>
                          <p className="text-xs font-semibold text-slate-200 leading-snug">
                            {item.description}
                          </p>
                          <p className="text-[9px] text-slate-500 font-mono tracking-tight">
                            Colección: {item.collectionName} | ID: {item.targetId}
                          </p>
                        </div>
                        
                        <button
                          onClick={() => dataService.removePendingAction(item.id)}
                          className="text-slate-500 hover:text-red-400 p-1 rounded-sm hover:bg-slate-800 transition-all opacity-0 group-hover:opacity-100 cursor-pointer shrink-0"
                          title="Eliminar de la cola de sincronización"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-slate-800 bg-slate-950 space-y-2.5">
                {pendingActions.length > 0 && (
                  <button
                    onClick={async () => {
                      await dataService.syncPendingActions();
                    }}
                    className="w-full bg-amber-500 hover:bg-amber-400 text-slate-950 py-2.5 rounded-lg text-xs font-bold uppercase tracking-wider font-mono flex items-center justify-center gap-2 cursor-pointer shadow-md active:scale-98 transition-all"
                  >
                    <RefreshCw className="h-4 w-4 animate-spin-slow" />
                    <span>Sincronizar ahora ({pendingActions.length})</span>
                  </button>
                )}
                
                <button
                  onClick={() => setIsSyncQueueOpen(false)}
                  className="w-full bg-slate-800 hover:bg-slate-700 text-slate-300 py-2 rounded-lg text-xs font-bold uppercase tracking-wider font-mono cursor-pointer transition-all"
                >
                  Cerrar Cola
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* MODAL DE CONFIGURACIÓN AVANZADA DE NOTIFICACIONES DC-3 (STPS) */}
      <DC3NotificationConfigModal
        isOpen={isDC3ConfigModalOpen}
        onClose={() => setIsDC3ConfigModalOpen(false)}
        currentUser={currentUser}
        onSaveSettings={handleSaveDC3NotificationSettings}
        dc3Records={allDC3Records}
        costCenters={costCenters}
      />

      {/* iOS NATIVE FLOATING BOTTOM TAB BAR (MOBILE ONLY) */}
      <div 
        className="md:hidden fixed bottom-0 left-0 right-0 z-40 ios-glass-bar border-t border-[#3a3d3a]/80 shadow-[0_-8px_25px_rgba(0,0,0,0.4)] px-2 pt-1.5 pb-[calc(env(safe-area-inset-bottom,0px)+6px)] flex items-center justify-around gpu-layer select-none"
        id="ios-bottom-nav-bar"
      >
        {/* 1. INICIO */}
        <button
          type="button"
          onClick={() => {
            if ('vibrate' in navigator) {
              try { navigator.vibrate(8); } catch (e) {}
            }
            setActiveTab('welcome');
            setExpandedClientSidebarId(null);
            setActiveInternalSubmenu(null);
            setIsMobileMenuOpen(false);
          }}
          className={`flex-1 flex flex-col items-center justify-center py-1 rounded-xl transition-all ios-press-feedback cursor-pointer ${
            activeTab === 'welcome' && !isMobileMenuOpen
              ? 'text-brand-red font-bold'
              : 'text-slate-400 hover:text-slate-200'
          }`}
          aria-label="Inicio"
        >
          <div className="relative">
            <Home className={`h-5 w-5 transition-transform ${activeTab === 'welcome' && !isMobileMenuOpen ? 'scale-110 text-brand-red stroke-[2.5]' : 'stroke-[1.75]'}`} />
            {activeTab === 'welcome' && !isMobileMenuOpen && (
              <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-brand-red rounded-full shadow-xs" />
            )}
          </div>
          <span className="text-[9.5px] mt-0.5 tracking-tight">Inicio</span>
        </button>

        {/* 2. HISTORIAL DE FOLIOS */}
        <button
          type="button"
          onClick={() => {
            if ('vibrate' in navigator) {
              try { navigator.vibrate(8); } catch (e) {}
            }
            const target = availableClientsForSidebar[0] || clients[0];
            if (target) setExpandedClientSidebarId(target.id);
            setActiveTab('client_submenu');
            setActiveClientSubmenu('historial');
            setActiveInternalSubmenu(null);
            setIsMobileMenuOpen(false);
          }}
          className={`flex-1 flex flex-col items-center justify-center py-1 rounded-xl transition-all ios-press-feedback cursor-pointer ${
            activeTab === 'client_submenu' && activeClientSubmenu === 'historial' && !isMobileMenuOpen
              ? 'text-brand-red font-bold'
              : 'text-slate-400 hover:text-slate-200'
          }`}
          aria-label="Historial de Folios"
        >
          <div className="relative">
            <History className={`h-5 w-5 transition-transform ${
              activeTab === 'client_submenu' && activeClientSubmenu === 'historial' && !isMobileMenuOpen
                ? 'scale-110 text-brand-red stroke-[2.5]'
                : 'stroke-[1.75]'
            }`} />
            {activeTab === 'client_submenu' && activeClientSubmenu === 'historial' && !isMobileMenuOpen && (
              <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-brand-red rounded-full shadow-xs" />
            )}
          </div>
          <span className="text-[9.5px] mt-0.5 tracking-tight truncate max-w-[65px]">Historial</span>
        </button>

        {/* 3. TRAZABILIDAD (FOLIOS / ESCANEO) */}
        <button
          type="button"
          onClick={() => {
            if ('vibrate' in navigator) {
              try { navigator.vibrate(8); } catch (e) {}
            }
            const target = availableClientsForSidebar[0] || clients[0];
            if (target) setExpandedClientSidebarId(target.id);
            setActiveTab('client_submenu');
            setActiveClientSubmenu('trazabilidad');
            setActiveInternalSubmenu(null);
            setIsMobileMenuOpen(false);
          }}
          className={`flex-1 flex flex-col items-center justify-center py-1 rounded-xl transition-all ios-press-feedback cursor-pointer ${
            activeTab === 'client_submenu' && activeClientSubmenu === 'trazabilidad' && !isMobileMenuOpen
              ? 'text-brand-red font-bold'
              : 'text-slate-400 hover:text-slate-200'
          }`}
          aria-label="Trazabilidad"
        >
          <div className="relative">
            <Compass className={`h-5 w-5 transition-transform ${
              activeTab === 'client_submenu' && activeClientSubmenu === 'trazabilidad' && !isMobileMenuOpen
                ? 'scale-110 text-brand-red stroke-[2.5]'
                : 'stroke-[1.75]'
            }`} />
            {activeTab === 'client_submenu' && activeClientSubmenu === 'trazabilidad' && !isMobileMenuOpen && (
              <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-brand-red rounded-full shadow-xs" />
            )}
          </div>
          <span className="text-[9.5px] mt-0.5 tracking-tight">Trazabilidad</span>
        </button>

        {/* 4. PO / FACTURAS */}
        <button
          type="button"
          onClick={() => {
            if ('vibrate' in navigator) {
              try { navigator.vibrate(8); } catch (e) {}
            }
            const target = availableClientsForSidebar[0] || clients[0];
            if (target) setExpandedClientSidebarId(target.id);
            setActiveTab('client_submenu');
            setActiveClientSubmenu('facturas_cobros');
            setActiveInternalSubmenu(null);
            setIsMobileMenuOpen(false);
          }}
          className={`flex-1 flex flex-col items-center justify-center py-1 rounded-xl transition-all ios-press-feedback cursor-pointer ${
            activeTab === 'client_submenu' && activeClientSubmenu === 'facturas_cobros' && !isMobileMenuOpen
              ? 'text-brand-red font-bold'
              : 'text-slate-400 hover:text-slate-200'
          }`}
          aria-label="Facturas y PO"
        >
          <div className="relative">
            <FileText className={`h-5 w-5 transition-transform ${
              activeTab === 'client_submenu' && activeClientSubmenu === 'facturas_cobros' && !isMobileMenuOpen
                ? 'scale-110 text-brand-red stroke-[2.5]'
                : 'stroke-[1.75]'
            }`} />
            {activeTab === 'client_submenu' && activeClientSubmenu === 'facturas_cobros' && !isMobileMenuOpen && (
              <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-brand-red rounded-full shadow-xs" />
            )}
          </div>
          <span className="text-[9.5px] mt-0.5 tracking-tight">POs / Cobros</span>
        </button>

        {/* 5. MENÚ COMPLETO (DRAWER TRIGGER) */}
        <button
          type="button"
          onClick={() => {
            if ('vibrate' in navigator) {
              try { navigator.vibrate(8); } catch (e) {}
            }
            setIsMobileMenuOpen(!isMobileMenuOpen);
          }}
          className={`flex-1 flex flex-col items-center justify-center py-1 rounded-xl transition-all ios-press-feedback cursor-pointer ${
            isMobileMenuOpen
              ? 'text-brand-red font-bold'
              : 'text-slate-400 hover:text-slate-200'
          }`}
          aria-label="Menú"
        >
          <div className="relative">
            {isMobileMenuOpen ? (
              <X className="h-5 w-5 scale-110 text-brand-red stroke-[2.5] transition-transform" />
            ) : (
              <Menu className="h-5 w-5 stroke-[1.75] transition-transform" />
            )}
            {isMobileMenuOpen && (
              <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-brand-red rounded-full shadow-xs" />
            )}
          </div>
          <span className="text-[9.5px] mt-0.5 tracking-tight">{isMobileMenuOpen ? 'Cerrar' : 'Menú'}</span>
        </button>
      </div>

    </div>
  );
}
